
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {SpinnerComponent} from 'react-element-spinner'
import {
	Button,
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	Col,
	Container,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalHeader,
	Row,
	Table
} from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter, faFileExcel, faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import { AuctionsContext } from '../../../contexts/AuctionsProvider'
import AuctionsService from '../../../services/AuctionsService'
import AuctionTabsService from '../../../services/AuctionTabsService'
import DocumentsService from '../../../services/DocumentsService'
import { SessionContext } from '../../../contexts/SessionProvider'
import PageTitleContextMenu from '../../../components/PageTitles/PageTitleContextMenu'
import Paginator from '../../../components/Paginator/Paginator'
import Enumeration from '../../../enumeration'
import VehicleInspectionsService from '../../../services/VehicleInspectionsService'
import SiteService from '../../../services/SiteService'
import ReportingService from '../../../services/ReportingService'
import AdministrationService from '../../../services/AdministrationService'
import { trigger } from '../../../events/customEvents'
import { InspectionsContext } from '../../../contexts/InspectionsProvider'
import MissingPricesModal from '../../../components/Modals/MissingPricesModal'
import HoldAuctionModal from '../../../components/Modals/HoldAuctionModal'
import VoidSaleModal from '../../../components/Modals/VoidSaleModal'
import PricingHistoryModal from '../../../components/Modals/PricingHistoryModal'
import BidDetailsModal from '../../../components/Modals/BidDetailsModal'
import CounterOfferModal from '../../../components/Modals/CounterOfferModal'
import SellConfirmationModal from '../../../components/Modals/SellConfirmationModal'
import RelistModal from '../../../components/Modals/RelistModal'
import MoveToShippingModal from '../../../components/Modals/MoveToShippingModal'
import MoveToPhysicalAuctionModal from '../../../components/Modals/MoveToPhysicalAuctionModal'
import UngroundModal from '../../../components/Modals/UngroundModal'
import ReleaseFromHoldModal from '../../../components/Modals/ReleaseFromHoldModal'
import ReleaseToAuctionModal from '../../../components/Modals/ReleaseToAuctionModal'
import BulkReleaseToAuctionModal from '../../../components/Modals/BulkReleaseToAuctionModal'
import CancelAuctionModal from '../../../components/Modals/CancelAuctionModal'
import LocationPopover from '../../../components/Popovers/LocationPopover'
import BuyerPopover from '../../../components/Popovers/BuyerPopover'
import CarfaxWarningIndicator from '../../../components/Popovers/CarfaxWarningIndicator'
import SellerInventoryTabs from '../../../components/Auctions/SellerInventoryTabs'
import SellerTabStatusFilter from '../../../components/Auctions/SellerTabStatusFilter'
import SearchAuctions from '../../../components/Auctions/SearchAuctions'
import IterationBadge from '../../../components/Auctions/IterationBadge'
import AuctionCountdownTimer from '../../../components/Auctions/AuctionCountdownTimer'

import AuctionsNavBar from '../../../components/Navbars/AuctionsNavBar'
import AuctionDetail from './components/AuctionDetail'
import AuctionBids from './components/AuctionBids'
import AuctionVehicle from './components/AuctionVehicle'
import AuctionOptions from './components/AuctionOptions'
import AuctionCondition from './components/AuctionCondition'
import AuctionDamages from './components/AuctionDamages'
import AuctionPictures from './components/AuctionPictures'
import AuctionDisclosures from './components/AuctionDisclosures'

import './AuctionsListingTable.scss'

const AuctionsListing = () =>
{
	const navigate = useNavigate()
	const { t } = useTranslation()
	const [isBusy, setIsBusy] = React.useState(false)
	const sessionContext = useContext(SessionContext)
	const auctionsContext = useContext(AuctionsContext)
	const [pagingResult, setPagingResult] = React.useState({ pageCollection: [], totalRecords: 0, isModified: false })
	const [enums, setEnums] = useState({})
	const inspectionsContext = useContext(InspectionsContext)
	const [openMissingPricesModal, setOpenMissingPricesModal] = useState(false)
	const [priceValidationMessages, setPriceValidationMessages] = useState([])
	const [showFilters, setShowFilters] = useState(false)
	const [isAuctionDetailModalOpen, setIsAuctionDetailModalOpen] = useState(false)
	const [activeDetailComponent, setActiveDetailComponent] = useState('auction-detail')
	const [viewModel, setViewModel] = useState({ makes: [], models: [], provinces: [], vehicleYearsFrom: undefined, vehicleYearsTo: undefined })
	const [auctionStates, setAuctionStates] = useState([])
	const defaultSearchRequest = { pageIndex: 0, pageSize: 15, serialNumber: '', auctionStateId: undefined, makes: [], models: [], yearFrom: undefined, yearTo: undefined, odometerFrom: undefined, odometerTo: undefined, priceFrom: undefined, priceTo: undefined, provinces: [], trim: '', daysInInventoryFrom: undefined, daysInInventoryTo: undefined, sortColumn: 'VIN', sortDirection: 'ASC' }

	// Sold tab specific state
	const [soldDateFrom, setSoldDateFrom] = useState(() => {
		const d = new Date()
		d.setDate(d.getDate() - 90)
		return d
	})
	const [soldDateTo, setSoldDateTo] = useState(() => new Date())
	const [soldSearchText, setSoldSearchText] = useState('')
	const [soldAllResults, setSoldAllResults] = useState([]) // Unfiltered results from API

	// Tab badge counts state
	const [tabCounts, setTabCounts] = useState({ pending: null, auction: null, 'bid-review': null, sold: null })

	// Action modal state
	const [actionTargetAuction, setActionTargetAuction] = useState(null)
	const [showHoldModal, setShowHoldModal] = useState(false)
	const [showVoidSaleModal, setShowVoidSaleModal] = useState(false)
	const [showPricingHistoryModal, setShowPricingHistoryModal] = useState(false)
	const [showBidDetailsModal, setShowBidDetailsModal] = useState(false)
	const [showCounterOfferModal, setShowCounterOfferModal] = useState(false)
	const [counterAtReserve, setCounterAtReserve] = useState(false)
	const [showSellConfirmationModal, setShowSellConfirmationModal] = useState(false)
	const [showRelistModal, setShowRelistModal] = useState(false)
	const [showMoveToShippingModal, setShowMoveToShippingModal] = useState(false)
	const [showMoveToPhysicalAuctionModal, setShowMoveToPhysicalAuctionModal] = useState(false)
	const [showUngroundModal, setShowUngroundModal] = useState(false)
	const [showReleaseFromHoldModal, setShowReleaseFromHoldModal] = useState(false)
	const [showReleaseToAuctionModal, setShowReleaseToAuctionModal] = useState(false)
	const [showCancelAuctionModal, setShowCancelAuctionModal] = useState(false)

	// BRD: Bulk Release to Auction - selection state
	const [selectedAuctionIds, setSelectedAuctionIds] = useState(new Set())
	const [showBulkReleaseModal, setShowBulkReleaseModal] = useState(false)

	// Request counter to prevent stale API responses from overwriting newer results (race condition guard)
	const searchRequestCounterRef = useRef(0)

	const renderMissing = (text = '—') => (
		<span className="auction-td-muted">{ text }</span>
	)

	const formatCurrency = (value) =>
	{
		if (value === undefined || value === null || value === '' || isNaN(Number(value))) return ''
		return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
	}

	const formatNumber = (value) =>
	{
		if (value === undefined || value === null || value === '' || isNaN(Number(value))) return ''
		return Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })
	}

	// Helper functions for new flat API structure (/AuctionTabs/* endpoints)
	const getVdpLabel = (pageItem) =>
	{
		if (pageItem?.yearMakeModel) return pageItem.yearMakeModel
		if (pageItem?.year && pageItem?.make && pageItem?.model)
		{
			return `${pageItem.year}/${pageItem.make}/${pageItem.model}`
		}
		return ''
	}

	const getTrim = (pageItem) => pageItem?.trim || ''
	const getVin = (pageItem) => pageItem?.vin || pageItem?.serialNumber || ''
	const getOemBank = (pageItem) => pageItem?.oemName || ''
	const getOdometer = (pageItem) =>
	{
		const mileage = pageItem?.odometer ?? pageItem?.mileage
		return mileage ? Number(mileage).toLocaleString() : ''
	}
	const getLocation = (pageItem) =>
	{
		// Pending API: lastKnownLocation is full city/province string
		if (pageItem?.lastKnownLocation) return pageItem.lastKnownLocation
		// Other APIs: locationCity, locationProvince at top level
		if (pageItem?.locationCity || pageItem?.locationProvince)
		{
			return [pageItem.locationCity, pageItem.locationProvince].filter(Boolean).join(', ')
		}
		return pageItem?.location || ''
	}
	const getDateGrounded = (pageItem) =>
	{
		// Bid Review API: groundingDate; Other APIs: dateGrounded
		const grounded = pageItem?.groundingDate ?? pageItem?.dateGrounded
		return grounded ? dayjs(grounded).format('MMM DD, YYYY') : ''
	}
	const getAuctionEndTime = (pageItem) =>
	{
		// New API: auctionEndTime at top level (actual field name)
		const endTime = pageItem?.auctionEndTime ?? pageItem?.auctionEndDate ?? pageItem?.auctionLocalEndTime
		return endTime ? dayjs(endTime).format('MMM DD, YYYY HH:mm') : ''
	}
	const getStatusName = (pageItem) =>
	{
		// Use auctionStateLabel mapping for proper display names (e.g., Released → Live)
		const statusId = pageItem?.auctionStatusId ?? pageItem?.statusId ?? pageItem?.auctionStateId
		if (statusId !== undefined && statusId !== null)
		{
			const label = auctionStateLabel(statusId)
			if (label) return label
		}
		// Fallback to API-provided name
		return pageItem?.vehicleStatusName || pageItem?.auctionStatusName || pageItem?.statusName || ''
	}
	const getIterationName = (pageItem) => pageItem?.currentIteration || pageItem?.iterationName || ''
	const getDaysInInventory = (pageItem) => pageItem?.daysInInventory ?? ''
	const getBidCount = (pageItem) => pageItem?.bidsCount ?? pageItem?.bidCount ?? ''
	const getBiddersCount = (pageItem) => pageItem?.biddersCount ?? ''
	const getHighBid = (pageItem) => pageItem?.highBid ?? pageItem?.currentHighBid ?? null

	const getActiveTabKey = () => auctionsContext.sellerInventoryTab || 'auction'

	// BRD: Bulk Release - Check if auction is eligible for bulk selection (Awaiting Seller Release, not on hold)
	const isEligibleForBulkRelease = (pageItem) => {
		// Check numeric status ID
		const statusId = pageItem?.auctionStatusId ?? pageItem?.statusId ?? pageItem?.auctionStateId
		const isAwaitingReleaseById = statusId === Enumeration.AuctionStates.AwaitingRelease || statusId === 4
		
		// Also check status name string (API may return string instead of ID)
		const statusName = (pageItem?.auctionStatusName || pageItem?.statusName || pageItem?.vehicleStatusName || '').toLowerCase()
		const isAwaitingReleaseByName = statusName === 'awaitingrelease' || statusName === 'awaiting release' || statusName === 'awaiting seller release'
		
		const isAwaitingRelease = isAwaitingReleaseById || isAwaitingReleaseByName
		const isOnHold = pageItem?.isOnHold === true
		return isAwaitingRelease && !isOnHold
	}

	// BRD: Bulk Release - Toggle single selection
	const handleToggleBulkSelection = (auctionId, e) => {
		e.stopPropagation()
		setSelectedAuctionIds(prev => {
			const newSet = new Set(prev)
			if (newSet.has(auctionId)) {
				newSet.delete(auctionId)
			} else {
				newSet.add(auctionId)
			}
			return newSet
		})
	}

	// BRD: Bulk Release - Select/Deselect all eligible items on current page
	const handleToggleSelectAll = () => {
		const eligibleItems = pagingResult.pageCollection.filter(isEligibleForBulkRelease)
		const eligibleIds = eligibleItems.map(item => item.auctionId)
		
		const allSelected = eligibleIds.every(id => selectedAuctionIds.has(id))
		
		setSelectedAuctionIds(prev => {
			const newSet = new Set(prev)
			if (allSelected) {
				// Deselect all on this page
				eligibleIds.forEach(id => newSet.delete(id))
			} else {
				// Select all eligible on this page
				eligibleIds.forEach(id => newSet.add(id))
			}
			return newSet
		})
	}

	// BRD: Bulk Release - Clear selection
	const handleClearBulkSelection = () => {
		setSelectedAuctionIds(new Set())
	}

	// BRD: Bulk Release - Get selected auctions for modal
	const getSelectedAuctionsForBulkRelease = () => {
		return pagingResult.pageCollection.filter(item => selectedAuctionIds.has(item.auctionId))
	}

	// BRD: Bulk Release - Check if all eligible items on page are selected
	const areAllEligibleSelected = useMemo(() => {
		const eligibleItems = pagingResult.pageCollection.filter(isEligibleForBulkRelease)
		if (eligibleItems.length === 0) return false
		return eligibleItems.every(item => selectedAuctionIds.has(item.auctionId))
	}, [pagingResult.pageCollection, selectedAuctionIds])

	// BRD: Bulk Release - Count of eligible items on current page
	const eligibleForBulkReleaseCount = useMemo(() => {
		return pagingResult.pageCollection.filter(isEligibleForBulkRelease).length
	}, [pagingResult.pageCollection])

	const getListingColumnsForTab = useMemo(() =>
	{
		/**
		 * Column contract:
		 * - key: stable unique
		 * - header: JSX
		 * - cell: (pageItem, pageItemIndex) => JSX
		 */
		const commonRowClickProps = (pageItem) => ({
			style: { cursor: 'pointer', fontSize: 'smaller' },
			onClick: () => handleSelectAuction(pageItem),
		})

			const auctionColumns = [
			// BRD: Checkbox column for bulk release (only visible for eligible items)
			{
				key: 'bulkSelectCheckbox',
				header: (
					<div className="text-center">
						<Input
							type="checkbox"
							checked={areAllEligibleSelected && eligibleForBulkReleaseCount > 0}
							onChange={handleToggleSelectAll}
							title="Select all eligible vehicles for bulk release"
							style={{ cursor: 'pointer' }}
							disabled={eligibleForBulkReleaseCount === 0}
						/>
					</div>
				),
				cell: (pageItem) => {
					const isEligible = isEligibleForBulkRelease(pageItem)
					const isSelected = selectedAuctionIds.has(pageItem.auctionId)
					return (
						<td className="text-center" style={{ width: '40px' }}>
							{isEligible ? (
								<Input
									type="checkbox"
									checked={isSelected}
									onChange={(e) => handleToggleBulkSelection(pageItem.auctionId, e)}
									onClick={(e) => e.stopPropagation()}
									style={{ cursor: 'pointer' }}
								/>
							) : (
								<span className="text-muted">—</span>
							)}
						</td>
					)
				}
			},
			{
				key: 'dateDaysStatus',
				sortKey: 'DateGrounded',
				header: (
					<>
						<div className="auction-header-stack">
							<div>{ t('Auctions.Listing.Date_Grounded') }</div>
							<div>{ t('Auctions.Listing.Days_In_Inventory') }</div>
							<div>{ t('Auctions.Listing.Status') }</div>
						</div>
					</>
				),
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }}>
						{ getDateGrounded(pageItem) }<br />
						{ getDaysInInventory(pageItem) || renderMissing() }<br />
						{ getStatusName(pageItem) || renderMissing() }
						{ pageItem?.isOnHold && <span className="badge bg-warning ms-1">Hold</span> }
					</td>
				)
			},
			{
				key: 'ymmTrimVinOem',
				sortKey: 'VIN',
				header: (
					<>
						<div className="auction-header-stack">
							<div>{ t('Auctions.Listing.Year_Make_Model') }</div>
							<div>{ t('Auctions.Listing.Trim') }</div>
							<div>{ t('Auctions.Listing.Vin') }</div>
							<div>{ t('Auctions.Listing.Oem_Bank') }</div>
						</div>
					</>
				),
				cell: (pageItem) => (
					<td { ...commonRowClickProps(pageItem) }>
						<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
							{ pageItem?.hasInspectionReport && (
								<span 
									className="badge bg-info"
									style={{ fontSize: '10px', padding: '2px 4px', cursor: 'pointer' }}
									title="Inspection Report Available"
								>
									<i className="bi bi-clipboard-check"></i>
								</span>
							)}
							{ getVdpLabel(pageItem) || renderMissing() }
						</div>
						{ getTrim(pageItem) || renderMissing() }<br />
						{ getVin(pageItem) || renderMissing() }<br />
						{ getOemBank(pageItem) || renderMissing() }
					</td>
				)
			},
			{
				key: 'bidsBidders',
				sortKey: 'BidsCount',
				header: <span>{ t('Auctions.Listing.Bids') }</span>,
				cell: (pageItem) => (
					<td className="auction-td-number auction-td-mono">
						{ getBidCount(pageItem) ? (
							<span 
								style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
								onClick={(e) => handleActionClick('BidDetails', pageItem, e)}
								title="Click to view bid details"
							>
								{ formatNumber(getBidCount(pageItem)) }
								{ /* BRD wants bids/bidders like 4/2; bidders count from new API biddersCount */ }
								{ getBiddersCount(pageItem) > 0 && <span className="auction-td-muted">/{ getBiddersCount(pageItem) }</span> }
							</span>
						) : renderMissing() }
					</td>
				)
			},
			{
				key: 'highBid',
				sortKey: 'HighBid',
				header: (
					<div className="auction-header-stack">
						<div>{ t('Auctions.Listing.High_Bid') }</div>
						<div>Bidder</div>
					</div>
				),
				cell: (pageItem) => {
					// New API: highBid; Old API: highestBidAmount
					const highBidValue = getHighBid(pageItem)
					// Check both top-level and nested reservePrice (API returns it in different locations depending on endpoint)
					const reservePrice = pageItem?.reservePrice ?? pageItem?.auctionVehicle?.reservePrice ?? 0
					// BRD: Reserve Met Indicator - show when high bid >= reserve price
					const isReserveMet = pageItem?.isReserveMet || (reservePrice && highBidValue && highBidValue >= reservePrice)
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-number auction-td-mono">
							{ highBidValue ? (
								<>
									<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
										{ formatCurrency(highBidValue) }
										{ reservePrice > 0 && (
											<span 
												className={`badge ${isReserveMet ? 'bg-success' : 'bg-warning'}`}
												style={{ fontSize: '9px', padding: '2px 4px' }}
												title={isReserveMet ? 'Reserve Met' : 'Reserve Not Met'}
											>
												{ isReserveMet ? '✓' : '!' }
											</span>
										)}
									</div>
									<div style={{ fontSize: 'x-small', whiteSpace: 'nowrap' }}>
										{ pageItem?.highBidderName || renderMissing('N/A') }
									</div>
									<span 
										style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline', fontSize: 'x-small' }}
										onClick={(e) => handleActionClick('BidDetails', pageItem, e)}
										title="View bid history and details"
									>
										Details
									</span>
								</>
							) : renderMissing() }
						</td>
					)
				}
			},
			{
				key: 'priceBlock',
				header: <span>{ t('Auctions.Listing.Price') }</span>,
				cell: (pageItem, pageItemIndex) => {
					// BRD: MBP, Start, Reserve, Buy Now (in that order)
					const mbpPrice = pageItem?.mbpPrice ?? ''
					const startPrice = pageItem?.startPrice ?? ''
					const reservePrice = pageItem?.reservePrice ?? ''
					const buyNowPrice = pageItem?.buyNowPrice ?? ''
					return (
						<td>
							<div className="row">
								<Label className="auction-listing-price-label" md="4">MBP</Label>
								<Col md="8"><Input name={`mbp-price-${pageItemIndex}`} type="text" className="auction-listing-price-input" value={ mbpPrice } onChange={ handlePricePropertyChange } /></Col>
							</div>
							<div className="row">
								<Label className="auction-listing-price-label" md="4">{t('Auctions.Listing.Start')}</Label>
								<Col md="8"><Input name={`start-price-${pageItemIndex}`} type="text" className="auction-listing-price-input" value={ startPrice } onChange={ handlePricePropertyChange } /></Col>
							</div>
							<div className="row">
								<Label className="auction-listing-price-label" md="4">{t('Auctions.Listing.Reserve')}</Label>
								<Col md="8"><Input name={`reserve-price-${pageItemIndex}`} type="text" className="auction-listing-price-input" value={ reservePrice } onChange={ handlePricePropertyChange } /></Col>
							</div>
							<div className="row">
								<Label className="auction-listing-price-label" md="4">{t('Auctions.Listing.Buy_Now')}</Label>
								<Col md="8"><Input name={`buy-now-price-${pageItemIndex}`} type="text" className="auction-listing-price-input" value={ buyNowPrice } onChange={ handlePricePropertyChange } /></Col>
							</div>
						</td>
					)
				}
			},
			{
				key: 'odometerGradeDamagesCarfax',
				sortKey: 'Odometer',
				header: (
					<>
						<div className="auction-header-stack">
							<div>{ t('Auctions.Listing.Odometer') }</div>
							<div>{ t('Auctions.Listing.Grade') }</div>
							<div>EWT</div>
							<div>{ t('Auctions.Listing.Damages') }</div>
						</div>
					</>
				),
				cell: (pageItem) => {
					const damageValue = pageItem?.damages ?? pageItem?.damageAmount
					const carfaxLink = pageItem?.auctionVehicle?.vehicle?.carfaxEnglishReportLink
					const hasCarfax = !!carfaxLink || pageItem?.hasCarfax || pageItem?.carfaxClaimsAmount > 0 || pageItem?.carfaxEstimatesAmount > 0
					const ewtValue = pageItem?.ewt ?? pageItem?.EWT
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-text">
							{ getOdometer(pageItem) || renderMissing() }<br />
							{ pageItem?.grade ?? pageItem?.autoGrade ?? renderMissing() }<br />
							{ ewtValue != null ? formatCurrency(ewtValue) : renderMissing() }<br />
							{ damageValue != null ? formatCurrency(damageValue) : renderMissing() }
							{ hasCarfax && (
								<CarfaxWarningIndicator
									id={`carfax-auction-${pageItem?.auctionId}`}
									hasCarfax={hasCarfax}
									carfaxAmount={pageItem?.carfaxTotalAmount ?? pageItem?.carfaxAmount}
									claimsAmount={pageItem?.carfaxClaimsAmount}
									estimatesAmount={pageItem?.carfaxEstimatesAmount}
									claimsCount={pageItem?.carfaxClaimsCount}
									incidentDescription={pageItem?.carfaxIncidentDescription}
								/>
							)}
						</td>
					)
				}
			},
			{
				key: 'location',
				header: <span>{ t('Auctions.Listing.Location') }</span>,
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-text">
						<LocationPopover
							id={`location-auction-${pageItem?.auctionId}`}
							location={getLocation(pageItem)}
							locationName={pageItem?.locationName}
							address={pageItem?.locationAddress}
							city={pageItem?.locationCity}
							province={pageItem?.locationProvince}
							postalCode={pageItem?.locationPostalCode}
							contactName={pageItem?.locationContact}
							contactPhone={pageItem?.locationPhone}
						/>
					</td>
				)
			},
			{
				key: 'residualCbbAvg',
				header: (
					<>
						<div className="auction-header-stack">
							<div>{ t('Auctions.Listing.Residual_Price') }</div>
							<div>{ t('Auctions.Listing.Cbb_Price') }</div>
							<div>{ t('Auctions.Listing.Average_Price') }</div>
							<div>Price</div>
						</div>
					</>
				),
				cell: (pageItem) => {
					// BRD: If less than 5 VINs for average, show asterisk (*) to indicate limited sample size
					const hasLimitedSample = pageItem?.hasLimitedSampleSize === true || pageItem?.averageSampleCount < 5
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-text">
							{ pageItem?.residual ? formatCurrency(pageItem.residual) : renderMissing() }<br />
							{ pageItem?.cbb ? formatCurrency(pageItem.cbb) : renderMissing() }<br />
							{ pageItem?.averagePrice ? (
								<span>
									{formatCurrency(pageItem.averagePrice)}
									{hasLimitedSample && <span title="Limited sample size (less than 5 VINs)" style={{ color: '#dc3545' }}>*</span>}
								</span>
							) : renderMissing() }<br />
							{ pageItem?.suggestedPrice ? formatCurrency(pageItem.suggestedPrice) : renderMissing() }
						</td>
					)
				}
			},
			{
				key: 'watchers',
				header: <div className="table-header-wrap">{ t('Auctions.Listing.Number_Watchers') }</div>,
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-number auction-td-mono">
						{ pageItem?.watchersCount ? formatNumber(pageItem.watchersCount) : renderMissing() }
					</td>
				)
			},
			{
				key: 'dealerViews',
				header: <div className="table-header-wrap">{ t('Auctions.Listing.Dealer_Views') }</div>,
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-number auction-td-mono">
						{ pageItem?.dealerViewsCount ? formatNumber(pageItem.dealerViewsCount) : 0 }
					</td>
				)
			},
			{
				key: 'auctionEndTime',
				sortKey: 'AuctionEndTime',
				header: (
					<div className="auction-header-stack">
						<div>{ t('Auctions.Listing.Auction_End_Time') }</div>
						<div>Iteration</div>
					</div>
				),
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-text">
						{/* BRD: Countdown clock for current tier (falls back to overall auction end) */}
						<AuctionCountdownTimer
							endTime={pageItem?.tierEndTime ?? pageItem?.auctionEndTime}
							secondsRemaining={pageItem?.secondsRemaining}
							isExtended={pageItem?.isExtended}
						/>
						{/* BRD: Show iteration (MBP, IT2, IT3 etc) */}
						<div className="mt-1">
							<IterationBadge
								iteration={pageItem?.currentIteration}
								iterationId={pageItem?.iterationId}
								size="small"
								showTooltip
							/>
						</div>
					</td>
				)
			},
			// BRD Column 12: Actions (with Hold/Release toggle per BRD)
			{
				key: 'actions',
				header: <div className="table-header-wrap">Action</div>,
				cell: (pageItem) => {
					const actions = pageItem?.availableActions || []
					const hasAction = (action) => actions.includes(action)
					const isOnHold = pageItem?.isOnHold === true
					// BRD: AwaitingRelease (id=4) = "Awaiting Seller Release" - shows "Release to Auction" action
					const isAwaitingSellerRelease = pageItem?.auctionStatusId === 4
					// BRD: Pre-auction statuses should not show "Relist" (only for post-auction statuses)
					const isPreAuctionStatus = [2, 3, 4].includes(pageItem?.auctionStatusId) // AwaitingInspection, AwaitingPricing, AwaitingRelease
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-text">
							<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
								{ hasAction('Notes') && <span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('Notes', pageItem, e)}>Notes</span> }
								{ hasAction('StatusPricingHistory') && <span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('StatusPricingHistory', pageItem, e)}>Status/Pricing History</span> }
								{ hasAction('Edit') && <span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('Edit', pageItem, e)}>Edit</span> }
								{ /* BRD Phase 4: Show Release if on hold, otherwise show Hold */ }
								{ isOnHold && <span style={{ color: '#28a745', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('Release', pageItem, e)}>Release</span> }
								{ !isOnHold && hasAction('Hold') && <span style={{ color: '#ffc107', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('Hold', pageItem, e)}>Hold</span> }
								{ /* BRD: Release to Auction - for units in Awaiting Seller Release status */ }
								{ isAwaitingSellerRelease && !isOnHold && <span style={{ color: '#28a745', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('ReleaseToAuction', pageItem, e)}>Release to Auction</span> }
								{ /* BRD: Relist only for post-auction statuses, not pre-auction */ }
								{ hasAction('Relist') && !isPreAuctionStatus && <span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('Relist', pageItem, e)}>Relist</span> }
								{ hasAction('Cancel') && <span style={{ color: '#dc3545', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('Cancel', pageItem, e)}>Cancel</span> }
								{ actions.length === 0 && !isOnHold && !isAwaitingSellerRelease && renderMissing() }
							</div>
						</td>
					)
				}
			},
		]

		// BRD Bid Review Tab - 12 columns per screenshot
		const bidReviewColumns = [
			// Col 1: Grounded Date / Days in Inventory
			{
				key: 'groundingDateDays',
				sortKey: 'GroundingDate',
				header: (
					<div className="auction-header-stack">
						<div>Grounded Date</div>
						<div>Days in Inventory</div>
					</div>
				),
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-text">
						{ getDateGrounded(pageItem) || renderMissing('N/A') }<br />
						{ getDaysInInventory(pageItem) ?? renderMissing('N/A') }
					</td>
				)
			},
			// Col 2: Year/Make/Model / Trim / VIN / OEM/Bank
			{
				key: 'ymmTrimVinOem',
				header: (
					<div className="auction-header-stack">
						<div>Year/Make/Model</div>
						<div>Trim</div>
						<div>VIN</div>
						<div>OEM/Bank</div>
					</div>
				),
				cell: (pageItem) => (
					<td { ...commonRowClickProps(pageItem) }>
						<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
							{ pageItem?.hasInspectionReport && (
								<span 
									className="badge bg-info"
									style={{ fontSize: '10px', padding: '2px 4px', cursor: 'pointer' }}
									title="Inspection Report Available"
								>
									<i className="bi bi-clipboard-check"></i>
								</span>
							)}
							{ getVdpLabel(pageItem) || renderMissing('N/A') }
						</div>
						{ getTrim(pageItem) || renderMissing('N/A') }<br />
						{ getVin(pageItem) || renderMissing('N/A') }<br />
						{ getOemBank(pageItem) || renderMissing('N/A') }
					</td>
				)
			},
			// Col 3: Location
			{
				key: 'location',
				header: <div className="table-header-wrap">Location</div>,
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-text">
						<LocationPopover
							id={`location-bidreview-${pageItem?.auctionId}`}
							location={getLocation(pageItem)}
							locationName={pageItem?.locationName}
							address={pageItem?.locationAddress}
							city={pageItem?.locationCity}
							province={pageItem?.locationProvince}
							postalCode={pageItem?.locationPostalCode}
							contactName={pageItem?.locationContact}
							contactPhone={pageItem?.locationPhone}
						/>
					</td>
				)
			},
			// Col 4: Odometer / Grade / EWT / Damages / CARFAX (per BRD)
			{
				key: 'odometerGradeDamagesCarfax',
				header: (
					<div className="auction-header-stack">
						<div>Odometer</div>
						<div>Grade</div>
						<div>EWT</div>
						<div>Damages</div>
					</div>
				),
				cell: (pageItem) => {
					const damageValue = pageItem?.damages ?? pageItem?.damageAmount
					const carfaxLink = pageItem?.auctionVehicle?.vehicle?.carfaxEnglishReportLink
					const hasCarfax = !!carfaxLink || pageItem?.hasCarfax || pageItem?.carfaxClaimsAmount > 0 || pageItem?.carfaxEstimatesAmount > 0
					const ewtValue = pageItem?.ewt ?? pageItem?.EWT
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-text">
							{ getOdometer(pageItem) || renderMissing('N/A') }<br />
							{ pageItem?.grade ?? pageItem?.autoGrade ?? renderMissing('N/A') }<br />
							{ ewtValue != null ? formatCurrency(ewtValue) : renderMissing('N/A') }<br />
							{ damageValue != null ? formatCurrency(damageValue) : renderMissing('N/A') }
							{ hasCarfax && (
								<CarfaxWarningIndicator
									id={`carfax-bidreview-${pageItem?.auctionId}`}
									hasCarfax={hasCarfax}
									carfaxAmount={pageItem?.carfaxTotalAmount ?? pageItem?.carfaxAmount}
									claimsAmount={pageItem?.carfaxClaimsAmount}
									estimatesAmount={pageItem?.carfaxEstimatesAmount}
									claimsCount={pageItem?.carfaxClaimsCount}
								/>
							)}
						</td>
					)
				}
			},
			// Col 5: Dealer Views
			{
				key: 'dealerViews',
				header: <div className="table-header-wrap">Dealer Views</div>,
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-number auction-td-mono">
						{ pageItem?.dealerViewsCount ? formatNumber(pageItem.dealerViewsCount) : renderMissing('N/A') }
					</td>
				)
			},
			// Col 6: Bids / Bidders
			{
				key: 'bidsBidders',
				header: <div className="table-header-wrap">Bids/Bidders</div>,
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-number auction-td-mono">
						{ getBidCount(pageItem) ? (
							<span 
								style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
								onClick={(e) => handleActionClick('BidDetails', pageItem, e)}
								title="Click to view bid details"
							>
								{ formatNumber(getBidCount(pageItem)) }
								{ getBiddersCount(pageItem) > 0 && <span className="auction-td-muted">/{ getBiddersCount(pageItem) }</span> }
							</span>
						) : renderMissing('N/A') }
					</td>
				)
			},
			// Col 7: High Bid / Variance (combined per BRD)
			{
				key: 'highBidVariance',
				header: (
					<div className="auction-header-stack">
						<div>High Bid</div>
						<div>Bidder</div>
						<div>Variance</div>
					</div>
				),
				cell: (pageItem) => {
					const highBidValue = getHighBid(pageItem)
					// Check both top-level and nested reservePrice (API returns it in different locations depending on endpoint)
					const reservePrice = pageItem?.reservePrice ?? pageItem?.auctionVehicle?.reservePrice ?? 0
					let variance = pageItem?.variance
					if (variance == null) {
						variance = (reservePrice && highBidValue) ? reservePrice - highBidValue : null
					}
					// BRD: Reserve Met Indicator - show when high bid >= reserve price
					const isReserveMet = pageItem?.isReserveMet || (reservePrice && highBidValue && highBidValue >= reservePrice)
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-number auction-td-mono">
							{ highBidValue ? (
								<>
									<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
										{ formatCurrency(highBidValue) }
										{ reservePrice > 0 && (
											<span 
												className={`badge ${isReserveMet ? 'bg-success' : 'bg-warning'}`}
												style={{ fontSize: '9px', padding: '2px 4px' }}
												title={isReserveMet ? 'Reserve Met' : 'Reserve Not Met'}
											>
												{ isReserveMet ? '✓' : '!' }
											</span>
										)}
									</div>
									<div style={{ fontSize: 'x-small', whiteSpace: 'nowrap' }}>
										{ pageItem?.highBidderName || renderMissing('N/A') }
									</div>
									<span 
										style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline', fontSize: 'x-small' }}
										onClick={(e) => handleActionClick('BidDetails', pageItem, e)}
										title="View bid history and details"
									>
										Details
									</span>
								</>
							) : renderMissing('N/A') }
							<br />
							{ variance != null ? formatCurrency(variance) : renderMissing('N/A') }
						</td>
					)
				}
			},
			// Col 8: Start Price
			{ 
				key: 'startPrice', 
				header: <div className="table-header-wrap">Start Price</div>, 
				cell: (pageItem) => {
					const startPrice = pageItem?.startPrice
					return (<td style={{ fontSize: 'smaller' }} className="auction-td-number auction-td-mono">{ startPrice ? formatCurrency(startPrice) : renderMissing('N/A') }</td>)
				}
			},
			// Col 9: Reserve Price
			{ 
				key: 'reservePrice', 
				header: <div className="table-header-wrap">Reserve Price</div>, 
				cell: (pageItem) => {
					const reservePrice = pageItem?.reservePrice
					return (<td style={{ fontSize: 'smaller' }} className="auction-td-number auction-td-mono">{ reservePrice ? formatCurrency(reservePrice) : renderMissing('N/A') }</td>)
				}
			},
			// Col 10: Counter Amount / Previous Counter (combined per BRD)
			{
				key: 'counterPrevious',
				header: (
					<div className="auction-header-stack">
						<div>Counter Amount</div>
						<div>Previous Counter</div>
					</div>
				),
				cell: (pageItem) => {
					const counter = pageItem?.counterAmount ?? pageItem?.counterOfferAmount
					const prevCounter = pageItem?.previousCounter ?? pageItem?.previousCounterOfferAmount
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-number auction-td-mono">
							{ counter != null ? formatCurrency(counter) : renderMissing('N/A') }<br />
							{ prevCounter != null ? formatCurrency(prevCounter) : renderMissing('N/A') }
						</td>
					)
				}
			},
			// Col 11: Time Remaining / Current Iteration (combined per BRD)
			{
				key: 'timeIterations',
				header: (
					<div className="auction-header-stack">
						<div>Time Remaining</div>
						<div>Current Iteration</div>
					</div>
				),
				cell: (pageItem) => {
					let timeText = renderMissing('N/A')
					if (pageItem?.secondsRemaining != null && pageItem.secondsRemaining > 0) {
						const hours = Math.floor(pageItem.secondsRemaining / 3600)
						const minutes = Math.floor((pageItem.secondsRemaining % 3600) / 60)
						timeText = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
					} else {
						const expiryDate = pageItem?.counterExpiryTime ?? pageItem?.counterOfferExpiry ?? pageItem?.auctionEndDate
						if (expiryDate) {
							const now = dayjs()
							const expiry = dayjs(expiryDate)
							const diffHours = expiry.diff(now, 'hour')
							const diffMinutes = expiry.diff(now, 'minute') % 60
							timeText = diffHours > 0 ? `${diffHours}h ${diffMinutes}m` : (diffMinutes > 0 ? `${diffMinutes}m` : 'Expired')
						}
					}
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-text auction-td-mono">
							{ timeText }<br />
							{ getIterationName(pageItem) ? <IterationBadge iteration={getIterationName(pageItem)} size="small" showTooltip /> : renderMissing('N/A') }
						</td>
					)
				}
			},
			// Col 12: Actions
			{
				key: 'actions',
				header: <div className="table-header-wrap">Actions</div>,
				cell: (pageItem) => {
					const actions = pageItem?.availableActions || []
					const hasAction = (action) => actions.includes(action)
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-text">
							<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
								{ hasAction('Sell') && <span style={{ color: '#28a745', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('Sell', pageItem, e)}>Sell</span> }
								{ hasAction('Counter') && <span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('Counter', pageItem, e)}>Counter</span> }
								{ hasAction('CounterAtReserve') && <span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('CounterAtReserve', pageItem, e)}>Counter at Reserve</span> }
								{ hasAction('Notes') && <span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('Notes', pageItem, e)}>Notes</span> }
								{ hasAction('ViewHistory') && <span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('ViewHistory', pageItem, e)}>View History</span> }
								{ actions.length === 0 && renderMissing('N/A') }
							</div>
						</td>
					)
				}
			},
		]
		// BRD Sold Tab Column Groupings:
		// Group 1: Sale Date, Iteration Sold
		// Group 2: Year/Make/Model/Trim, VIN, OEM/Bank
		// Group 3: Odometer, Grade, Location, Purchase Location
		// Group 4: Bids, Offers
		// Group 5: Purchase Price
		// Group 6: Buyer, Dealer Code
		// Group 7: Information (Confirmation of Sale, Release Form, Pricing History, Void Sale)
		const soldColumns = [
			// Group 1: Sale Date / Iteration Sold
			{
				key: 'saleDateIteration',
				sortKey: 'SaleDate',
				header: (
					<div className="auction-header-stack">
						<div>Sale Date</div>
						<div>Iteration Sold</div>
					</div>
				),
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-text auction-td-mono">
						{ pageItem?.saleDate ? dayjs(pageItem.saleDate).format('YYYY-MM-DD') : renderMissing('N/A') }<br />
						{ pageItem?.iterationSold ? <IterationBadge iteration={pageItem.iterationSold} size="small" showTooltip /> : renderMissing('N/A') }
					</td>
				)
			},
			// Group 2: Year/Make/Model/Trim, VIN, OEM/Bank
			{
				key: 'ymmTrimVinOem',
				sortKey: 'VIN',
				header: (
					<div className="auction-header-stack">
						<div>Year/Make/Model/Trim</div>
						<div>VIN</div>
						<div>OEM/Bank</div>
					</div>
				),
				cell: (pageItem) => (
					<td { ...commonRowClickProps(pageItem) }>
						<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
							{ pageItem?.hasInspectionReport && (
								<span 
									className="badge bg-info"
									style={{ fontSize: '10px', padding: '2px 4px', cursor: 'pointer' }}
									title="Inspection Report Available"
								>
									<i className="bi bi-clipboard-check"></i>
								</span>
							)}
							{ getVdpLabel(pageItem) || renderMissing('N/A') }{ getTrim(pageItem) ? ` ${getTrim(pageItem)}` : '' }
						</div>
						{ getVin(pageItem) || renderMissing('N/A') }<br />
						{ getOemBank(pageItem) || renderMissing('N/A') }
					</td>
				)
			},
			// Group 3: Odometer, Grade, Location, Purchase Location
			{
				key: 'odometerGradeLocation',
				header: (
					<div className="auction-header-stack">
						<div>Odometer</div>
						<div>Grade</div>
						<div>Location</div>
						<div>Purchase Location</div>
					</div>
				),
				cell: (pageItem) => {
					// Sold API: purchaseLocationCity, purchaseLocationProvince
					const purchaseLocation = (pageItem?.purchaseLocationCity || pageItem?.purchaseLocationProvince)
						? [pageItem.purchaseLocationCity, pageItem.purchaseLocationProvince].filter(Boolean).join(', ')
						: pageItem?.purchaseLocation
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-text auction-td-mono">
							{ getOdometer(pageItem) || renderMissing('N/A') }<br />
							{ pageItem?.grade || renderMissing('N/A') }<br />
							<LocationPopover
								id={`location-sold-${pageItem?.auctionId}`}
								location={getLocation(pageItem)}
								locationName={pageItem?.locationName}
								address={pageItem?.locationAddress}
								city={pageItem?.locationCity}
								province={pageItem?.locationProvince}
								postalCode={pageItem?.locationPostalCode}
								contactName={pageItem?.locationContact}
								contactPhone={pageItem?.locationPhone}
							/><br />
							{ purchaseLocation || renderMissing('N/A') }
						</td>
					)
				}
			},
			// Group 4: Bids, Offers (per BRD)
			{
				key: 'bidsOffers',
				header: (
					<div className="auction-header-stack">
						<div>Bids</div>
						<div>Offers</div>
					</div>
				),
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-number auction-td-mono">
						{ getBidCount(pageItem) ?? 0 }<br />
						{ pageItem?.offersCount ?? 0 }
					</td>
				)
			},
			// Group 5: Purchase Price
			{
				key: 'purchasePrice',
				header: <div className="table-header-wrap">Purchase Price</div>,
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-number auction-td-mono">
						{ pageItem?.purchasePrice != null ? formatCurrency(pageItem.purchasePrice) : renderMissing('N/A') }
					</td>
				)
			},
			// Group 6: Buyer, Dealer Code (buyerName, buyerCode from new API) - with BuyerPopover per BRD
			{
				key: 'buyerDealerCode',
				header: (
					<div className="auction-header-stack">
						<div>Buyer</div>
						<div>Dealer Code</div>
					</div>
				),
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller', whiteSpace: 'nowrap' }} className="auction-td-text">
						<BuyerPopover
							id={`buyer-sold-${pageItem?.auctionId}`}
							buyerName={pageItem?.buyerName ?? pageItem?.buyer}
							dealerCode={pageItem?.buyerCode ?? pageItem?.dealerCode}
							address={pageItem?.buyerAddress}
							city={pageItem?.buyerCity}
							province={pageItem?.buyerProvince}
							postalCode={pageItem?.buyerPostalCode}
							contactPhone={pageItem?.buyerPhone}
							contactEmail={pageItem?.buyerEmail}
						/><br />
						{ pageItem?.buyerCode ?? pageItem?.dealerCode ?? renderMissing('N/A') }
					</td>
				)
			},
			// Group 7: Information (action items) - uses availableActions array and boolean flags
			{
				key: 'information',
				header: <div className="table-header-wrap">Information</div>,
				cell: (pageItem) => {
					// Sold API: availableActions is array like ["StatusPricingHistory", "VoidSale"]
					const actions = pageItem?.availableActions || []
					const hasAction = (action) => actions.includes(action)
					const canPricingHistory = hasAction('StatusPricingHistory') || hasAction('PricingHistory')
					const canVoidSale = hasAction('VoidSale') && !pageItem?.isVoided
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-text">
							<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
								{ pageItem?.hasConfirmationOfSale && pageItem?.confirmationOfSaleFileName ? (
									<span 
										style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
										onClick={(e) => { 
											e.stopPropagation()
											DocumentsService
												.getBase64Document({
													storeTypeId: Enumeration.StorageTypes.ConfirmationOfSales,
													fileName: pageItem.confirmationOfSaleFileName
												}, sessionContext.loginSession.user.userSession.sessionKey)
												.then(result => {
													let pdfWindow = window.open('')
													pdfWindow.document.write(
														"<iframe width='100%' height='100%' src='data:application/pdf;base64, " +
														encodeURI(result.data.fileContent) + "'></iframe>"
													)
												})
												.catch(err => console.error('Error downloading Confirmation of Sale:', err))
										}}
									>Confirmation of Sale</span>
								) : <span className="auction-td-muted">Confirmation of Sale</span> }
								{ pageItem?.auctionSaleId && !pageItem?.isVoided ? (
									<span
										style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
										onClick={(e) => handleActionClick('RegenerateCos', pageItem, e)}
									>Regenerate COS</span>
								) : null }
								{ pageItem?.hasReleaseForm && pageItem?.releaseFormUrl ? (
									<span 
										style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
										onClick={(e) => { e.stopPropagation(); window.open(pageItem.releaseFormUrl, '_blank') }}
									>Release Form</span>
								) : <span className="auction-td-muted">Release Form</span> }
								{ canPricingHistory ? (
									<span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('PricingHistory', pageItem, e)}>Pricing History</span>
								) : <span className="auction-td-muted">Pricing History</span> }
								{ pageItem?.isVoided ? (
									<span className="auction-td-muted">Voided{ pageItem?.voidReason ? `: ${pageItem.voidReason}` : '' }</span>
								) : canVoidSale ? (
									<span style={{ color: '#dc3545', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('VoidSale', pageItem, e)}>Void Sale</span>
								) : null }
							</div>
						</td>
					)
				}
			},
		]
		// BRD Pending Tab - 6 columns per screenshot
		const pendingColumns = [
			// Col 1: Date Grounded / Date of Last Status Change (per BRD)
			{
				key: 'datesGroundedStatus',
				sortKey: 'DateGrounded',
				header: (
					<div className="auction-header-stack">
						<div>Date Grounded</div>
						<div>Date of Last Status Change</div>
					</div>
				),
				cell: (pageItem) => {
					const lastStatusChange = pageItem?.lastStatusChangeDate 
						? dayjs(pageItem.lastStatusChangeDate).format('YYYY-MM-DD') 
						: renderMissing('N/A')
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-text auction-td-mono">
							{ getDateGrounded(pageItem) || renderMissing('N/A') }<br />
							{ lastStatusChange }
						</td>
					)
				}
			},
			// Col 2: Vehicle Status (separate column per BRD)
			{
				key: 'vehicleStatus',
				sortKey: 'VehicleStatus',
				header: <div className="table-header-wrap">Vehicle Status</div>,
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-text">
						{ getStatusName(pageItem) || renderMissing('N/A') }
					</td>
				)
			},
			// Col 3: Year/Make/Model / Trim / VIN / OEM/Bank
			{
				key: 'ymmTrimVinOem',
				sortKey: 'VIN',
				header: (
					<div className="auction-header-stack">
						<div>Year/Make/Model</div>
						<div>Trim</div>
						<div>VIN</div>
						<div>OEM/Bank</div>
					</div>
				),
				cell: (pageItem) => (
					<td { ...commonRowClickProps(pageItem) }>
						<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
							{ pageItem?.hasInspectionReport && (
								<span 
									className="badge bg-info"
									style={{ fontSize: '10px', padding: '2px 4px', cursor: 'pointer' }}
									title="Inspection Report Available"
								>
									<i className="bi bi-clipboard-check"></i>
								</span>
							)}
							{ getVdpLabel(pageItem) || renderMissing('N/A') }
						</div>
						{ getTrim(pageItem) || renderMissing('N/A') }<br />
						{ getVin(pageItem) || renderMissing('N/A') }<br />
						{ getOemBank(pageItem) || renderMissing('N/A') }
					</td>
				)
			},
			// Col 4: Odometer / Grade / Damages (combined per BRD)
			{
				key: 'odometerGradeDamages',
				header: (
					<div className="auction-header-stack">
						<div>Odometer</div>
						<div>Grade</div>
						<div>Damages</div>
					</div>
				),
				cell: (pageItem) => {
					const damageValue = pageItem?.damages ?? pageItem?.damageAmount
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-text auction-td-mono">
							{ getOdometer(pageItem) || renderMissing('N/A') }<br />
							{ pageItem?.grade || renderMissing('N/A') }<br />
							{ damageValue != null ? formatCurrency(damageValue) : renderMissing('N/A') }
						</td>
					)
				}
			},
			// Col 5: Location with LocationPopover
			{
				key: 'location',
				header: <div className="table-header-wrap">Location</div>,
				cell: (pageItem) => (
					<td style={{ fontSize: 'smaller' }} className="auction-td-text">
						<LocationPopover
							id={`location-pending-${pageItem?.auctionId || pageItem?.vehicleId}`}
							location={getLocation(pageItem)}
							locationName={pageItem?.locationName}
							address={pageItem?.locationAddress}
							city={pageItem?.locationCity}
							province={pageItem?.locationProvince}
							postalCode={pageItem?.locationPostalCode}
							contactName={pageItem?.locationContact}
							contactPhone={pageItem?.locationPhone}
						/>
					</td>
				)
			},
			// Col 6: Action (with conditional actions based on status per BRD)
			{
				key: 'actions',
				header: <div className="table-header-wrap">Action</div>,
				cell: (pageItem) => {
					const actions = pageItem?.availableActions || []
					const hasAction = (action) => actions.includes(action) || actions.includes(action.replace(/\s/g, ''))
					
					// Determine vehicle status for conditional actions per BRD
					const vehicleStatus = (pageItem?.vehicleStatus || pageItem?.statusName || '').toLowerCase()
					const isShipping = vehicleStatus.includes('shipping')
					const isUngrounded = vehicleStatus.includes('ungrounded')
					const isShippingOrUngrounded = isShipping || isUngrounded
					
					// Per BRD:
					// For Unsold, Physical Auction, Cancelled: Notes, Pricing History, Relist
					// For Shipping and Ungrounded: Notes and Pricing History (NO Relist)
					const canNotes = hasAction('Notes') || true // Always show Notes per BRD
					const canPricingHistory = hasAction('PricingHistory') || true // Always show Pricing History per BRD
					const canRelist = !isShippingOrUngrounded // Relist not available for Shipping/Ungrounded per BRD
					
					return (
						<td style={{ fontSize: 'smaller' }} className="auction-td-text">
							<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
								{ canNotes && <span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('Notes', pageItem, e)}>Notes</span> }
								{ canPricingHistory && <span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('PricingHistory', pageItem, e)}>Pricing History</span> }
								{ canRelist && <span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={(e) => handleActionClick('Relist', pageItem, e)}>Relist</span> }
							</div>
						</td>
					)
				}
			},
		]

		return {
			auction: auctionColumns,
			'bid-review': bidReviewColumns,
			sold: soldColumns,
			pending: pendingColumns,
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [t, auctionsContext, pagingResult, selectedAuctionIds])

	const activeColumns = () =>
	{
		const tabKey = getActiveTabKey()
		return getListingColumnsForTab[tabKey] || getListingColumnsForTab.auction
	}
	
	useEffect(() =>
	{
		setEnums(Enumeration)
		try
		{
			if (!auctionsContext.sellerInventoryTab)
			{
				auctionsContext.setSellerInventoryTab('auction')
			}
		}
		catch {}
		setAuctionStates([
			{ auctionStateId: 0, name: 'All' },
			{ auctionStateId: 1, name: 'Grounded' },
			{ auctionStateId: 2, name: 'Awaiting Inspection' },
			{ auctionStateId: 3, name: 'Awaiting Pricing' },
			{ auctionStateId: 4, name: 'Awaiting Release' },
			{ auctionStateId: 5, name: 'Live' },
			{ auctionStateId: 6, name: 'In Settlement' },
			{ auctionStateId: 7, name: 'Sold' },
			{ auctionStateId: 8, name: 'Complete Unsold' },
			{ auctionStateId: 9, name: 'Expired' },
			{ auctionStateId: 10, name: 'Cancelled' },
			{ auctionStateId: 11, name: 'Hold' },
			{ auctionStateId: 12, name: 'Shipping' },
			{ auctionStateId: 13, name: 'Physical Auction' },
			{ auctionStateId: 14, name: 'Ungrounded' },
		])
		const initialTabKey = auctionsContext.sellerInventoryTab || 'auction'
		// Reconcile the BRD status dropdown (sellerTabStatusFilter) into searchRequest
		// before firing the first query. These two state slots can desync when other
		// views (e.g. AuctionsSearch) reset searchRequest but leave the dropdown
		// alone — without this the grid would load all units while the dropdown
		// visually shows "Live".
		const persistedStatus = auctionsContext.sellerTabStatusFilter
		const reconciledStatusFilter = (!persistedStatus || persistedStatus === 'all') ? null : persistedStatus
		const reconciledInitialRequest = { ...auctionsContext.searchRequest, statusFilter: reconciledStatusFilter }
		if (reconciledInitialRequest.statusFilter !== auctionsContext.searchRequest?.statusFilter)
		{
			auctionsContext.setSearchRequest(reconciledInitialRequest)
		}
		// Use tab-aware search function for initial load
		retrieveTabSearchResults(initialTabKey, reconciledInitialRequest)
		// Always pass auctionStateId=0 so all Makes/Models/Provinces are returned for filter dropdowns
		retrieveFilterViewModel({ ...reconciledInitialRequest, auctionStateId: 0 })
		retrieveTabCounts()
	}, [])

	/**
	 * Fetch VIN counts for all tabs to display in tab badges.
	 * Called on mount and after status-changing actions.
	 */
	const retrieveTabCounts = () =>
	{
		const sessionKey = sessionContext.loginSession.user.userSession.sessionKey
		const oemId = auctionsContext.searchRequest?.oemId || null
		
		AuctionTabsService.getTabCounts(oemId, sessionKey)
			.then(result =>
			{
				sessionContext.updateSession(result.data.loginSession)
				const counts = result.data.actionResult
				// Map API response to tab keys
				// Expected response: { auctionCount, bidReviewCount, soldCount, pendingCount } or similar
				setTabCounts({
					pending: counts?.pendingCount ?? counts?.pending ?? null,
					auction: counts?.auctionCount ?? counts?.auction ?? null,
					'bid-review': counts?.bidReviewCount ?? counts?.bidReview ?? null,
					sold: counts?.soldCount ?? counts?.sold ?? null,
				})
			})
			.catch(() =>
			{
				// Silently fail - badge counts are not critical
			})
	}

	const retrieveFilterViewModel = (searchRequest) =>
	{
		// Best-effort: listing page reuses the same VM endpoint as search to populate filter options.
		AuctionsService
			.getBusinessSearchViewModel(searchRequest, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				sessionContext.updateSession(result.data.loginSession)
				setViewModel(result.data.actionResult)
			})
			.catch(() => {})
	}

	const pendingSubFilterToAuctionStateId = (subFilterKey) =>
	{
		// API supports a single AuctionStateId.
		// "All Pending" is represented as 0 (All) here as a pragmatic compromise.
		switch (subFilterKey)
		{
			case 'all':
				return 0
			case 'grounded':
				return Enumeration.AuctionStates.Grounded
			case 'awaiting-inspection':
				return Enumeration.AuctionStates.AwaitingInspection
			case 'awaiting-pricing':
				return Enumeration.AuctionStates.AwaitingPricing
			case 'awaiting-release':
				return Enumeration.AuctionStates.AwaitingRelease
			case 'cancelled':
				return Enumeration.AuctionStates.Cancelled
			default:
				return Enumeration.AuctionStates.Grounded
		}
	}

	const inventoryTabToAuctionStateId = (tabKey) =>
	{
		// Client-side grouping approximation for BRD tabs.
		// We map each tab to a single auctionStateId that the API already supports.
		switch (tabKey)
		{
			case 'pending':
				return pendingSubFilterToAuctionStateId(auctionsContext.sellerPendingSubFilter || 'all')
			case 'auction':
				return Enumeration.AuctionStates.Released
			case 'bid-review':
				return Enumeration.AuctionStates.InSettlement
			case 'sold':
				return Enumeration.AuctionStates.Sold
			default:
				return undefined
		}
	}

	const hasExplicitStatusFilter = (searchRequest) =>
	{
		const raw = searchRequest?.auctionStateId
		if (raw === undefined || raw === null || raw === '') return false
		return Number(raw) !== 0
	}

	const applyTabPresetToSearchRequest = (searchRequest, tabKey) =>
	{
		const presetStateId = inventoryTabToAuctionStateId(tabKey)
		if (!presetStateId) return searchRequest
		if (hasExplicitStatusFilter(searchRequest)) return searchRequest
		return { ...searchRequest, auctionStateId: presetStateId }
	}

	const handleInventoryTabChange = (tabKey) =>
	{
		auctionsContext.setSellerInventoryTab(tabKey)
		// Reset all filters when switching tabs — each tab has different data and status options
		try { auctionsContext.setSellerTabStatusFilter('all') } catch {}
		try { auctionsContext.setSellerPendingSubFilter('all') } catch {}
		const next = { ...defaultSearchRequest, auctionStateId: inventoryTabToAuctionStateId(tabKey), statusFilter: null }
		auctionsContext.setSearchRequest(next)
		// Pass auctionStateId=0 to VM so all Makes/Models/Provinces are returned (the tab search endpoint handles its own filtering)
		retrieveFilterViewModel({ ...next, auctionStateId: 0 })
		retrieveTabSearchResults(tabKey, next)
	}

	const handlePendingSubFilterChange = (subFilterKey) =>
	{
		try
		{
			auctionsContext.setSellerPendingSubFilter(subFilterKey)
		}
		catch {}

		if ((auctionsContext.sellerInventoryTab || 'auction') !== 'pending')
		{
			return
		}

		auctionsContext.setSearchRequest((previousSearchRequest) =>
		{
			const next = { ...previousSearchRequest, pageIndex: 0, auctionStateId: pendingSubFilterToAuctionStateId(subFilterKey) }
			retrieveTabSearchResults('pending', next)
			return next
		})
	}

	const handleTabStatusFilterChange = (statusKey) =>
	{
		try { auctionsContext.setSellerTabStatusFilter(statusKey) } catch {}
		const tabKey = auctionsContext.sellerInventoryTab || 'auction'
		auctionsContext.setSearchRequest((previousSearchRequest) =>
		{
			// Pass statusFilter string directly to API (not auctionStateId)
			const statusFilter = statusKey === 'all' ? null : statusKey
			const next = { ...previousSearchRequest, pageIndex: 0, statusFilter }
			retrieveTabSearchResults(tabKey, next)
			// Always pass auctionStateId=0 so all filter options remain available
			retrieveFilterViewModel({ ...next, auctionStateId: 0 })
			return next
		})
	}

	const handleClearFilters = () =>
	{
		const tabKey = auctionsContext.sellerInventoryTab || 'auction'
		try { auctionsContext.setSellerTabStatusFilter('all') } catch {}
		
		const next = { ...defaultSearchRequest, statusFilter: null }
		
		auctionsContext.setSearchRequest(next)
		retrieveFilterViewModel({ ...next, auctionStateId: 0 })
		retrieveTabSearchResults(tabKey, next)
	}

	/**
	 * Handle column sort click.
	 * Toggles direction if same column, or sets new column with ASC direction.
	 * @param {string} sortKey - The API field name to sort by
	 */
	const handleColumnSort = (sortKey) =>
	{
		if (!sortKey) return

		const tabKey = auctionsContext.sellerInventoryTab || 'auction'
		auctionsContext.setSearchRequest((previousSearchRequest) =>
		{
			const currentColumn = previousSearchRequest.sortColumn
			const currentDirection = previousSearchRequest.sortDirection || 'ASC'
			
			// Toggle direction if same column, else reset to ASC
			const newDirection = (currentColumn === sortKey && currentDirection === 'ASC') ? 'DESC' : 'ASC'
			
			const next = {
				...previousSearchRequest,
				sortColumn: sortKey,
				sortDirection: newDirection,
				pageIndex: 0, // Reset to first page on sort change
			}
			
			retrieveTabSearchResults(tabKey, next)
			return next
		})
	}

	const handleSearchParameterChange = (e) =>
	{
		const { name, value} = e.target
		auctionsContext.setSearchRequest((previousSearchRequest) => ({ ...previousSearchRequest, [name]: value }))
	}

	const handleStatusChange = (e) =>
	{
		const { name, value} = e.target
		const tabKey = auctionsContext.sellerInventoryTab || 'auction'
		auctionsContext.setSearchRequest((previousSearchRequest) =>
		{
			const next = { ...previousSearchRequest, [name]: value, pageIndex: 0 }
			retrieveTabSearchResults(tabKey, next)
			retrieveFilterViewModel({ ...next, auctionStateId: 0 })
			return next
		})
	}

	const handleFilterToggle = (fieldName, value) =>
	{
		auctionsContext.setSearchRequest((prev) => {
			const current = prev[fieldName] || []
			const newValues = current.includes(value)
				? current.filter(v => v !== value)
				: [...current, value]
			return { ...prev, [fieldName]: newValues }
		})
	}

	const handleMakesChange = (value) => handleFilterToggle('makes', value)
	const handleModelsChange = (value) => handleFilterToggle('models', value)
	const handleProvincesChange = (value) => handleFilterToggle('provinces', value)

	const handleSearchAuctions = () =>
	{
		// On listing page, "Search" just reloads results with current request.
		// Reconcile the BRD status dropdown into searchRequest so the button always
		// reflects what the user currently sees in the dropdown — guards against
		// the two state slots having drifted apart.
		const tabKey = auctionsContext.sellerInventoryTab || 'auction'
		const persistedStatus = auctionsContext.sellerTabStatusFilter
		const reconciledStatusFilter = (!persistedStatus || persistedStatus === 'all') ? null : persistedStatus
		const reconciled = { ...auctionsContext.searchRequest, statusFilter: reconciledStatusFilter, pageIndex: 0 }
		if (reconciled.statusFilter !== auctionsContext.searchRequest?.statusFilter
			|| reconciled.pageIndex !== auctionsContext.searchRequest?.pageIndex)
		{
			auctionsContext.setSearchRequest(reconciled)
		}
		retrieveTabSearchResults(tabKey, reconciled)
	}

	/**
	 * Retrieve search results using the /AuctionTabs/* API endpoints.
	 * Each tab has its own dedicated endpoint for optimized data retrieval.
	 * @param {string} tabKey - The active tab key ('auction', 'bid-review', 'sold', 'pending')
	 * @param {Object} searchRequest - The search parameters
	 */
	const retrieveTabSearchResults = (tabKey, searchRequest) =>
	{
		setIsBusy(true)
		const sessionKey = sessionContext.loginSession.user.userSession.sessionKey

		// Race condition guard: increment counter so stale responses from prior calls are discarded.
		// Without this, a slow response from a previous filter/reload can overwrite a newer result.
		const requestId = ++searchRequestCounterRef.current

		// Build the search request for the new API format
		// Property names must match AuctionTabSearchRequestDto exactly
		const apiRequest = {
			pageIndex: searchRequest.pageIndex || 0,
			pageSize: searchRequest.pageSize || 15,
			sortColumn: searchRequest.sortColumn || 'VIN',
			sortDirection: searchRequest.sortDirection || 'ASC',
			oemId: searchRequest.oemId || null,
			statusFilter: searchRequest.statusFilter || null,
			vin: searchRequest.serialNumber || null,
			makes: searchRequest.makes?.length ? searchRequest.makes : null,
			models: searchRequest.models?.length ? searchRequest.models : null,
			provinces: searchRequest.provinces?.length ? searchRequest.provinces : null,
			trim: searchRequest.trim || null,
			yearFrom: searchRequest.yearFrom || null,
			yearTo: searchRequest.yearTo || null,
			odometerFrom: searchRequest.odometerFrom || null,
			odometerTo: searchRequest.odometerTo || null,
			priceFrom: searchRequest.priceFrom || null,
			priceTo: searchRequest.priceTo || null,
			daysInInventoryFrom: searchRequest.daysInInventoryFrom || null,
			daysInInventoryTo: searchRequest.daysInInventoryTo || null,
			dateFrom: searchRequest.dateFrom || null,
			dateTo: searchRequest.dateTo || null,
		}

		// Add tab-specific parameters
		if (tabKey === 'sold')
		{
			apiRequest.saleDateFrom = soldDateFrom ? dayjs(soldDateFrom).format('YYYY-MM-DD') : null
			apiRequest.saleDateTo = soldDateTo ? dayjs(soldDateTo).format('YYYY-MM-DD') : null
			// Map status filter to isVoided - use searchRequest.statusFilter only (context state may be stale during tab switches)
			const statusFilter = searchRequest.statusFilter
			if (statusFilter === 'Voided' || statusFilter === 'voided') {
				apiRequest.isVoided = true
			} else if (statusFilter === 'Sold' || statusFilter === 'sold') {
				apiRequest.isVoided = false
			} else {
				// 'all' - don't filter by voided status
				apiRequest.isVoided = null
			}
		}
		else if (tabKey === 'pending')
		{
			// Map status filter to pendingStatus - use searchRequest.statusFilter only (context state may be stale during tab switches)
			const statusFilter = searchRequest.statusFilter
			if (statusFilter && statusFilter !== 'all')
			{
				const pendingStatusMap = {
					'unsold': 'Unsold',
					'shipping': 'Shipping',
					'physical-auction': 'PhysicalAuction',
					'physicalauction': 'PhysicalAuction',
					'cancelled': 'Cancelled',
					'ungrounded': 'Ungrounded',
				}
				// Only set pendingStatus if it's a valid enum value — never pass unknown strings
				// (e.g. 'Released' from a stale Auction tab filter would crash the backend)
				const mappedStatus = pendingStatusMap[statusFilter.toLowerCase()]
				if (mappedStatus) {
					apiRequest.pendingStatus = mappedStatus
				}
			}
		}

		// Select the appropriate service method based on tab
		let serviceMethod
		switch (tabKey)
		{
			case 'bid-review':
				serviceMethod = AuctionTabsService.searchBidReviewTab
				break
			case 'sold':
				serviceMethod = AuctionTabsService.searchSoldTab
				break
			case 'pending':
				serviceMethod = AuctionTabsService.searchPendingTab
				break
			case 'auction':
			default:
				serviceMethod = AuctionTabsService.searchAuctionTab
				break
		}

		serviceMethod(apiRequest, sessionKey)
			.then(result =>
			{
				// Discard stale response — a newer request has already been fired
				if (requestId !== searchRequestCounterRef.current) return

				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				// Map API response format to existing pagingResult shape
				// Backend returns: totalRecords, pageCollection (same as old API shape)
				const apiResult = result.data.actionResult
				const searchResults = (apiResult.pageCollection || apiResult.results || []).map((item) =>
				{
					// Mark as new API format for column renderers
					item._isNewApiFormat = true
					item.isModified = false
					return item
				})

				const pagingResult =
				{
					totalRecords: apiResult.totalRecords ?? apiResult.totalCount ?? 0,
					pageCollection: searchResults,
					isModified: false
				}

				setPagingResult(pagingResult)

				// For Sold tab, also store unfiltered results for client-side search
				if (tabKey === 'sold')
				{
					setSoldAllResults(searchResults)
				}
			})
			.catch((error) =>
			{
				// Only handle errors for the latest request
				if (requestId !== searchRequestCounterRef.current) return

				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	/**
	 * Client-side filter for Sold results by VIN or Buyer.
	 */
	const applySoldSearchFilter = (results, searchText) =>
	{
		if (!searchText || searchText.trim() === '') return results
		const lowerSearch = searchText.toLowerCase().trim()
		return results.filter(item => {
			const vin = (item?.vin || item?.serialNumber || '').toLowerCase()
			const buyer = (item?.buyerName || item?.buyer || '').toLowerCase()
			return vin.includes(lowerSearch) || buyer.includes(lowerSearch)
		})
	}

	/**
	 * Handle Sold tab search text change - client-side filtering.
	 */
	const handleSoldSearchTextChange = (e) =>
	{
		const text = e.target.value
		setSoldSearchText(text)
		// Apply filter to cached results
		const filtered = applySoldSearchFilter(soldAllResults, text)
		setPagingResult(prev => ({
			...prev,
			totalRecords: filtered.length,
			pageCollection: filtered
		}))
	}

	/**
	 * Handle Sold tab date range change - re-fetch from API.
	 */
	const handleSoldDateSearch = () =>
	{
		setSoldSearchText('') // Reset text filter on new date search
		retrieveTabSearchResults('sold', auctionsContext.searchRequest)
	}

	/**
	 * Export Auction tab data to CSV.
	 */
	const handleExportAuctionData = () =>
	{
		if (pagingResult.pageCollection.length === 0) {
			trigger('raiseNotification', { title: 'Export', message: 'No data to export.', type: 'warning', duration: 3 })
			return
		}
		
		const exportData = pagingResult.pageCollection.map(item => ({
			'Grounding Date': item.groundingDate ? dayjs(item.groundingDate).format('YYYY-MM-DD') : '',
			'Status': item.statusName || item.auctionStateName || '',
			'Year': item.year || item.modelYear || '',
			'Make': item.make || '',
			'Model': item.model || '',
			'Trim': item.trim || '',
			'VIN': item.vin || item.serialNumber || '',
			'Odometer': item.odometer || item.mileage || '',
			'Grade': item.grade || '',
			'OEM/Bank': item.oemName || '',
			'MBP': item.mbpPrice || '',
			'Reserve': item.reservePrice || '',
			'High Bid': item.highBid || item.highBidAmount || '',
			'On Hold': item.isOnHold ? 'Yes' : 'No',
			'Location': item.purchaseLocationCity && item.purchaseLocationProvince 
				? `${item.purchaseLocationCity}, ${item.purchaseLocationProvince}` 
				: item.purchaseLocationCity || item.purchaseLocationProvince || '',
			'Dealer Views': item.dealerViewsCount || 0,
			'Auction End': item.auctionEndTime ? dayjs(item.auctionEndTime).format('YYYY-MM-DD HH:mm') : '',
		}))
		
		ReportingService.downloadReportData(exportData, `AuctionVehicles_${dayjs().format('YYYY-MM-DD')}`)
		trigger('raiseNotification', { title: 'Export', message: 'Auction data exported successfully.', type: 'success', duration: 3 })
	}

	/**
	 * Export Bid Review tab data to CSV.
	 */
	const handleExportBidReviewData = () =>
	{
		if (pagingResult.pageCollection.length === 0) {
			trigger('raiseNotification', { title: 'Export', message: 'No data to export.', type: 'warning', duration: 3 })
			return
		}
		
		const exportData = pagingResult.pageCollection.map(item => ({
			'Grounding Date': item.groundingDate ? dayjs(item.groundingDate).format('YYYY-MM-DD') : '',
			'Days In Inventory': item.daysInInventory || '',
			'Auction End': item.auctionEndTime ? dayjs(item.auctionEndTime).format('YYYY-MM-DD HH:mm') : '',
			'Iteration': item.iterationName || '',
			'Year': item.year || item.modelYear || '',
			'Make': item.make || '',
			'Model': item.model || '',
			'Trim': item.trim || '',
			'VIN': item.vin || item.serialNumber || '',
			'Odometer': item.odometer || item.mileage || '',
			'Grade': item.grade || '',
			'OEM/Bank': item.oemName || '',
			'MBP': item.mbpPrice || '',
			'Reserve': item.reservePrice || '',
			'High Bid': item.highBid || item.highBidAmount || '',
			'Variance': item.reserveVariance || '',
			'Bids': item.bidsCount || 0,
			'Location': item.purchaseLocationCity && item.purchaseLocationProvince 
				? `${item.purchaseLocationCity}, ${item.purchaseLocationProvince}` 
				: item.purchaseLocationCity || item.purchaseLocationProvince || '',
		}))
		
		ReportingService.downloadReportData(exportData, `BidReviewVehicles_${dayjs().format('YYYY-MM-DD')}`)
		trigger('raiseNotification', { title: 'Export', message: 'Bid Review data exported successfully.', type: 'success', duration: 3 })
	}

	/**
	 * Export Pending tab data to CSV.
	 */
	const handleExportPendingData = () =>
	{
		if (pagingResult.pageCollection.length === 0) {
			trigger('raiseNotification', { title: 'Export', message: 'No data to export.', type: 'warning', duration: 3 })
			return
		}
		
		const exportData = pagingResult.pageCollection.map(item => ({
			'Grounding Date': item.groundingDate ? dayjs(item.groundingDate).format('YYYY-MM-DD') : '',
			'Last Status Change': item.lastStatusChangeDate ? dayjs(item.lastStatusChangeDate).format('YYYY-MM-DD') : '',
			'Status': item.vehicleStatus || item.statusName || '',
			'Year': item.year || item.modelYear || '',
			'Make': item.make || '',
			'Model': item.model || '',
			'Trim': item.trim || '',
			'VIN': item.vin || item.serialNumber || '',
			'Odometer': item.odometer || item.mileage || '',
			'Location': item.purchaseLocationCity && item.purchaseLocationProvince 
				? `${item.purchaseLocationCity}, ${item.purchaseLocationProvince}` 
				: item.purchaseLocationCity || item.purchaseLocationProvince || '',
		}))
		
		ReportingService.downloadReportData(exportData, `PendingVehicles_${dayjs().format('YYYY-MM-DD')}`)
		trigger('raiseNotification', { title: 'Export', message: 'Pending data exported successfully.', type: 'success', duration: 3 })
	}

	/**
	 * Export Sold tab data to CSV.
	 */
	const handleExportSoldData = () =>
	{
		if (pagingResult.pageCollection.length === 0) {
			trigger('raiseNotification', { title: 'Export', message: 'No data to export.', type: 'warning', duration: 3 })
			return
		}
		
		// Transform to flat structure for CSV export (using new API field names)
		const exportData = pagingResult.pageCollection.map(item => ({
			'Sale Date': item.saleDate ? dayjs(item.saleDate).format('YYYY-MM-DD') : '',
			'Iteration Sold': item.iterationSold || '',
			'Year': item.year || '',
			'Make': item.make || '',
			'Model': item.model || '',
			'Trim': item.trim || '',
			'VIN': item.vin || item.serialNumber || '',
			'Odometer': item.odometer || item.mileage || '',
			'Grade': item.grade || '',
			'Location': item.purchaseLocationCity && item.purchaseLocationProvince 
				? `${item.purchaseLocationCity}, ${item.purchaseLocationProvince}` 
				: item.purchaseLocationCity || item.purchaseLocationProvince || '',
			'Bids': item.bidsCount || 0,
			'Offers': item.offersCount || 0,
			'Purchase Price': item.purchasePrice || '',
			'Buyer': item.buyerName || '',
			'Dealer Code': item.dealerCode || '',
		}))
		
		ReportingService.downloadReportData(exportData, `SoldVehicles_${dayjs().format('YYYY-MM-DD')}`)
		trigger('raiseNotification', { title: 'Export', message: 'Sold data exported successfully.', type: 'success', duration: 3 })
	}

	/**
	 * Unified export handler - routes to appropriate tab export function.
	 */
	const handleExportTabData = () =>
	{
		const tabKey = auctionsContext.sellerInventoryTab || 'auction'
		switch (tabKey) {
			case 'auction':
				handleExportAuctionData()
				break
			case 'bid-review':
				handleExportBidReviewData()
				break
			case 'pending':
				handleExportPendingData()
				break
			case 'sold':
				handleExportSoldData()
				break
			default:
				trigger('raiseNotification', { title: 'Export', message: 'Export not available for this tab.', type: 'warning', duration: 3 })
		}
	}
	
	const handleGoBack = () =>
	{
		navigate('/secure/auctions-search')
	}
	
	const handleReload = () =>
	{
		// Use tab-aware search function for reload
		const tabKey = auctionsContext.sellerInventoryTab || 'auction'
		retrieveTabSearchResults(tabKey, auctionsContext.searchRequest)
		// Always pass auctionStateId=0 so all filter options are available
		retrieveFilterViewModel({ ...auctionsContext.searchRequest, auctionStateId: 0 })
		retrieveTabCounts()
	}
	
	const handlePageChange = (pageIndex) =>
	{
		const tabKey = auctionsContext.sellerInventoryTab || 'auction'
		auctionsContext.setSearchRequest((previousSearchRequest) =>
		{
			let currentSearchRequest = { ...previousSearchRequest }
			currentSearchRequest.pageIndex = pageIndex
			
			retrieveTabSearchResults(tabKey, currentSearchRequest)
			// Always pass auctionStateId=0 so all filter options are available
			retrieveFilterViewModel({ ...currentSearchRequest, auctionStateId: 0 })
			
			return currentSearchRequest
		})
	}
	
	const auctionStateLabel = (auctionStateId) =>
	{
		let _label = ''
		if (auctionStateId !== undefined)
		{
			switch(auctionStateId)
			{
				case enums.AuctionStates.Grounded:
					_label = 'Grounded'
					break
				case enums.AuctionStates.AwaitingInspection:
					_label = 'Awaiting Inspection'
					break
				case enums.AuctionStates.AwaitingPricing:
					_label = 'Awaiting Pricing'
					break
				case enums.AuctionStates.AwaitingRelease:
					_label = 'Awaiting Release'
					break
				case enums.AuctionStates.Released:
					_label = 'Live'
					break
				case enums.AuctionStates.InSettlement:
					_label = 'In Settlement'
					break
				case enums.AuctionStates.Sold:
					_label = 'Sold'
					break
				case enums.AuctionStates.CompleteUnsold:
					_label = 'Complete Unsold'
					break
				case enums.AuctionStates.Expired:
					_label = 'Expired'
					break
				case enums.AuctionStates.Cancelled:
					_label = 'Cancelled'
					break
				case enums.AuctionStates.Hold:
					_label = 'Hold'
					break
				case enums.AuctionStates.Shipping:
					_label = 'Shipping'
					break
				case enums.AuctionStates.PhysicalAuction:
					_label = 'Physical Auction'
					break
				case enums.AuctionStates.Ungrounded:
					_label = 'Ungrounded'
					break
			}
		}
		return _label
	}

	const handleSelectAuction = (auction) =>
	{
		// Fetch full auction details before opening the modal.
		// The flat API response from /AuctionTabs/* doesn't have the nested
		// structure (auctionVehicle.vehicleId) that modal components expect.
		setIsBusy(true)
		AuctionsService
			.getAuction(auction.auctionId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				sessionContext.updateSession(result.data.loginSession)
				const fullAuction = result.data.actionResult
				auctionsContext.setAuction(fullAuction)
				return retrieveInspection(fullAuction.auctionId)
			})
			.then(() =>
			{
				setIsBusy(false)
				// Keep the user on the listing page; show auction details in a modal.
				setActiveDetailComponent('auction-detail')
				setIsAuctionDetailModalOpen(true)
			})
			.catch(error =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}

	/**
	 * Centralized action click handler for all tab action links.
	 * Routes to deep-linked modal tabs or opens standalone action modals.
	 * @param {string} action - The action name (Notes, Cancel, Hold, etc.)
	 * @param {Object} pageItem - The auction item from the grid
	 * @param {Event} e - Click event (to prevent row click propagation)
	 */
	const handleActionClick = (action, pageItem, e) =>
	{
		e?.stopPropagation()

		/**
		 * Fetch full auction details and open modal to specified tab.
		 * The flat API response from /AuctionTabs/* doesn't have the nested
		 * structure (auctionVehicle.vehicle) that modal components expect,
		 * so we fetch the full auction before opening the modal.
		 */
		const setAuctionAndOpenModal = (detailComponent) =>
		{
			setIsBusy(true)
			AuctionsService
				.getAuction(pageItem.auctionId, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					sessionContext.updateSession(result.data.loginSession)
					const fullAuction = result.data.actionResult
					auctionsContext.setAuction(fullAuction)
					return retrieveInspection(fullAuction.auctionId)
				})
				.then(() =>
				{
					setIsBusy(false)
					setActiveDetailComponent(detailComponent)
					setIsAuctionDetailModalOpen(true)
				})
				.catch(error =>
				{
					setIsBusy(false)
					SiteService.displayApiError(error)
				})
		}

		switch (action)
		{
			// Deep-link actions - open Auction Detail modal to specific tab
			case 'Notes':
				setAuctionAndOpenModal('auction-disclosures')
				break

			case 'Edit':
				setAuctionAndOpenModal('auction-detail')
				break

			// BRD: Cancel opens standalone confirmation modal
			case 'Cancel':
				setActionTargetAuction(pageItem)
				setShowCancelAuctionModal(true)
				break

			case 'Accept':
			case 'ViewHistory':
			case 'BidDetails':
				// Deep-link to Bids & Offers tab in Auction Detail modal
				setAuctionAndOpenModal('auction-bids')
				break

			// NEW: Standalone modal for Sell confirmation
			case 'Sell':
				setActionTargetAuction(pageItem)
				setShowSellConfirmationModal(true)
				break

			// NEW: Standalone modal for Counter offer
			case 'Counter':
				setActionTargetAuction(pageItem)
				setCounterAtReserve(false)
				setShowCounterOfferModal(true)
				break

			// NEW: Standalone modal for Counter at Reserve
			case 'CounterAtReserve':
				setActionTargetAuction(pageItem)
				setCounterAtReserve(true)
				setShowCounterOfferModal(true)
				break

			// NEW: Standalone modal for Relist with iteration selection
			case 'Relist':
				setActionTargetAuction(pageItem)
				setShowRelistModal(true)
				break

			// Standalone modal actions
			case 'Hold':
				setActionTargetAuction(pageItem)
				setShowHoldModal(true)
				break

			// BRD Phase 4: Release from Hold
			case 'Release':
			case 'ReleaseHold':
				setActionTargetAuction(pageItem)
				setShowReleaseFromHoldModal(true)
				break

			// BRD: Release to Auction (from Awaiting Seller Release status)
			case 'ReleaseToAuction':
				setActionTargetAuction(pageItem)
				setShowReleaseToAuctionModal(true)
				break

			case 'StatusPricingHistory':
			case 'PricingHistory':
				setActionTargetAuction(pageItem)
				setShowPricingHistoryModal(true)
				break

			case 'VoidSale':
				setActionTargetAuction(pageItem)
				setShowVoidSaleModal(true)
				break

			// BRD Phase 3: Pending tab status transitions
			case 'MoveToShipping':
				setActionTargetAuction(pageItem)
				setShowMoveToShippingModal(true)
				break

			case 'MoveToPhysicalAuction':
				setActionTargetAuction(pageItem)
				setShowMoveToPhysicalAuctionModal(true)
				break

			case 'Unground':
				setActionTargetAuction(pageItem)
				setShowUngroundModal(true)
				break

			// Future actions - placeholder
			case 'ConfirmationOfSale':
			case 'ReleaseForm':
				trigger('raiseNotification', { title: 'Coming Soon', message: `The "${action}" feature is not yet implemented.`, type: 'info', duration: 5 })
				break

			case 'RegenerateCos':
				if (!pageItem?.auctionSaleId)
				{
					trigger('raiseNotification', { title: 'Unavailable', message: 'No sale record found for this auction.', type: 'warning', duration: 5 })
					break
				}
				if (!window.confirm(`Regenerate Confirmation of Sale for VIN ${pageItem?.vin ?? ''}? This will overwrite the existing PDF in storage.`))
				{
					break
				}
				AdministrationService
					.regenerateConfirmationOfSale(pageItem.auctionSaleId, sessionContext.loginSession.user.userSession.sessionKey)
					.then(() => {
						trigger('raiseNotification', { title: 'Success', message: 'Confirmation of Sale regenerated.', type: 'success', duration: 5 })
						handleReload()
					})
					.catch((err) => {
						console.error('Error regenerating Confirmation of Sale:', err)
						trigger('raiseNotification', { title: 'Error', message: 'Failed to regenerate Confirmation of Sale.', type: 'danger', duration: 7 })
					})
				break

			default:
				console.warn(`Unhandled action: ${action}`)
		}
	}

	/**
	 * Callback when action modal completes successfully.
	 * Closes modal and refreshes grid data.
	 */
	const handleActionModalSuccess = () =>
	{
		setShowHoldModal(false)
		setShowVoidSaleModal(false)
		setShowPricingHistoryModal(false)
		setShowBidDetailsModal(false)
		setShowCounterOfferModal(false)
		setShowSellConfirmationModal(false)
		setShowRelistModal(false)
		setShowReleaseToAuctionModal(false)
		setShowCancelAuctionModal(false)
		setCounterAtReserve(false)
		setActionTargetAuction(null)
		// Refresh the current tab data
		handleReload()
	}

	/**
	 * Callback when action modal is closed without action.
	 */
	const handleActionModalClose = () =>
	{
		setShowHoldModal(false)
		setShowVoidSaleModal(false)
		setShowPricingHistoryModal(false)
		setShowBidDetailsModal(false)
		setShowCounterOfferModal(false)
		setShowSellConfirmationModal(false)
		setShowRelistModal(false)
		setShowReleaseToAuctionModal(false)
		setCounterAtReserve(false)
		setActionTargetAuction(null)
	}

	const closeAuctionDetailModal = () =>
	{
		setIsAuctionDetailModalOpen(false)
	}

	const renderAuctionDetailContent = () =>
	{
		switch (activeDetailComponent)
		{
			case 'auction-bids':
				return <AuctionBids embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-vehicle':
				return <AuctionVehicle embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-options':
				return <AuctionOptions embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-condition':
				return <AuctionCondition embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-damages':
				return <AuctionDamages embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-pictures':
				return <AuctionPictures embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-disclosures':
				return <AuctionDisclosures embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
			case 'auction-detail':
			default:
				return <AuctionDetail embedded onRequestClose={ closeAuctionDetailModal } onSelectDetailTab={ setActiveDetailComponent } />
		}
	}
	
	const retrieveInspection = (auctionId) =>
	{
		return new Promise((resolve, reject) =>
		{
			setIsBusy(true)
			VehicleInspectionsService
				.getVehicleInspection(auctionId, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					setIsBusy(false)
					sessionContext.updateSession(result.data.loginSession)
					
					inspectionsContext.setVehicleInspection(result.data.actionResult)
					
					resolve()
				})
				.catch((error) =>
				{
					setIsBusy(false)
					reject(error)
				})
		})
	}
	
	const handlePricePropertyChange = (e) =>
	{
		const { name, value} = e.target
		
		const pricePropertyName = name.substring(0, name.lastIndexOf('-'))
		const pageItemIndex = parseInt(name.substring(name.lastIndexOf('-') + 1))
		
		setPagingResult((previousPagingResult) =>
		{
			const currentPagingResult = { ...previousPagingResult }
			const currentPageItem = { ...currentPagingResult.pageCollection[pageItemIndex] }
			
			// Allow empty string or valid number input
			const numericValue = value === '' ? null : parseFloat(value)
			const isValidInput = value === '' || !isNaN(numericValue)
			
			if (isValidInput)
			{
				switch (pricePropertyName)
				{
					case 'start-price':
						currentPageItem.startPrice = numericValue
						currentPageItem.isModified = true
						currentPagingResult.isModified = true
						break
					case 'reserve-price':
						currentPageItem.reservePrice = numericValue
						currentPageItem.isModified = true
						currentPagingResult.isModified = true
						break
					case 'buy-now-price':
						currentPageItem.buyNowPrice = numericValue
						currentPageItem.isModified = true
						currentPagingResult.isModified = true
						break
					case 'mbp-price':
					case 'market-price':
						// MBP Price (Market Based Price) - update both fields for compatibility
						currentPageItem.mbpPrice = numericValue
						currentPageItem.marketPrice = numericValue
						currentPageItem.isModified = true
						currentPagingResult.isModified = true
						break
					default:
						// No matching property, don't update
						return previousPagingResult
				}
				currentPagingResult.pageCollection[pageItemIndex] = currentPageItem
			}
			
			return currentPagingResult
		})
	}
	
	const auctionVehiclePriceValidation = () =>
	{
		/*
		Basic Pricing Relationships 🚩(WAITING FOR EDIT)🚩
		Start Price:  Must be > 0 and < Reserve Price
		Reserve Price: Must be > Start Price
		Buy Now Price: Must be > Reserve Price
		Market Price: Can be any value but should ideally be > Reserve Price

		~~Business Logic Validation 🚩(WAITING FOR EDIT)🚩~~
		Required:
		Start Price is required
		Reserve Price is required
		Optional:
		Buy Now Price is optional, but if filled: Must be greater than or equal to Reserve Price
		Market Price is optional and used for reference only?

		Conflict Checks 🚩(WAITING FOR EDIT)🚩
		Buy Now Price cannot be lower than Reserve or Start
		Reserve Price should not be equal Start Price if you want to encourage bidding
		Start Price = Reserve Price makes it a de facto fixed price auction - do we allow this?

		Edge Case Handling 🚩(WAITING FOR EDIT)🚩
		Zero Dollar Reserve? (No reserve auction) Should be explicitly flagged or confirmed by user if allowed.
		Buy Now Price set but lower than Market Price - Should not be allowed and trigger warning?
		Market Price way higher than Reserve - Should we trigger advisory?
		* */
		
		/* Email Aug 10, 2025
		How it should be:
			Start Price - required - Greater than 0
			Reserve Price - required - Greater than 0
			Buy Now  Price - optional
			Market Price - required only if there is an MBP tier or original dealer tier - Greater than 0
		*/
		
		const validationResult =
			{
				validationMessages: [],
				isValid: true
			}
		
		pagingResult.pageCollection.forEach((pageItem) =>
		{
			if (pageItem.isModified)
			{
				const vin = pageItem.vin || pageItem.serialNumber || 'Unknown VIN'
				
				let hasStartPrice = !isNaN(parseFloat(pageItem.startPrice)) && pageItem.startPrice > 0
				if (!hasStartPrice)
				{
					validationResult.validationMessages.push(`${vin}: The Start Price is invalid. It must be greater than zero.`)
					validationResult.isValid = false
				}
				
				let hasReservePrice = !isNaN(parseFloat(pageItem.reservePrice)) && pageItem.reservePrice > 0
				if (!hasReservePrice)
				{
					validationResult.validationMessages.push(`${vin}: The Reserve Price is invalid. It must be greater than zero.`)
					validationResult.isValid = false
				}
				
				let hasBuyNowPrice = true
				if (!isNaN(parseFloat(pageItem.buyNowPrice)))
				{
					hasBuyNowPrice = pageItem.buyNowPrice > 0
				}
				if (!hasBuyNowPrice)
				{
					validationResult.validationMessages.push(`${vin}: The Buy Now Price is invalid. If supplied it must be greater than zero.`)
					validationResult.isValid = false
				}
				
				const hasMarketPrice = (pageItem.hasMarketTier && (!isNaN(parseFloat(pageItem.marketPrice)) && pageItem.marketPrice > 0)) || !pageItem.hasMarketTier
				if ((pageItem.hasMarketTier || pageItem.hasOriginalDealerTier) && !hasMarketPrice)
				{
					validationResult.validationMessages.push(`${vin}: An MBP Tier or Original Dealer Tier exists but there is not a valid market price.`)
					validationResult.isValid = false
				}
			}
		})
		
		return validationResult
	}
	
	const handleUpdatePricing = () =>
	{
		const vehiclePriceValidation = auctionVehiclePriceValidation()
		
		if (vehiclePriceValidation.isValid)
		{
			setIsBusy(true)
			
			// Get all modified items
			const modifiedItems = pagingResult.pageCollection.filter(item => item.isModified)
			
			if (modifiedItems.length === 0) {
				setIsBusy(false)
				trigger('raiseNotification', { title: 'No Changes', message: 'No pricing changes to update.', type: 'info', duration: 3 })
				return
			}
			
			// Use AuctionTabsService.updatePricing for each modified item
			const updatePromises = modifiedItems.map(item => 
				AuctionTabsService.updatePricing(
					item.auctionId,
					item.mbpPrice,
					item.startPrice,
					item.reservePrice,
					item.buyNowPrice,
					item.mbpPrice, // sync MarketPrice = MBP
					null, // notes
					sessionContext.loginSession.user.userSession.sessionKey
				)
			)
			
			Promise.all(updatePromises)
				.then(results =>
				{
					setIsBusy(false)
					// Update session from last result
					if (results.length > 0) {
						sessionContext.updateSession(results[results.length - 1].data.loginSession)
					}
					
					setPagingResult((previousPagingResult) =>
					{
						const currentPagingResult = { ...previousPagingResult }
						currentPagingResult.isModified = false
						// Clear isModified flag on each item
						currentPagingResult.pageCollection = currentPagingResult.pageCollection.map(item => ({
							...item,
							isModified: false
						}))
						
						return currentPagingResult
					})
					
					// Refresh tab counts after status-changing action
					retrieveTabCounts()
					
					// Refresh the current tab data to reflect any status changes (e.g. AwaitingPricing → AwaitingRelease)
					const tabKey = auctionsContext.sellerInventoryTab || 'auction'
					retrieveTabSearchResults(tabKey, auctionsContext.searchRequest)
					
					trigger('raiseNotification', { title: 'Update Success', message: `${modifiedItems.length} vehicle(s) pricing was updated.`, type: 'success', duration: 5 })
				})
				.catch((error) =>
				{
					setIsBusy(false)
					SiteService.displayApiError(error)
				})
		}
		else
		{
			setPriceValidationMessages(vehiclePriceValidation.validationMessages)
			setOpenMissingPricesModal(true)
		}
	}
	
	return (
		<>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			<PageTitleContextMenu
				heading={ t('Auctions.Listing.Title') }
				subheading=""
				icon="pe-7s-ticket icon-gradient bg-premium-dark"
				headerOptions={ { showBack: true, backCommand: () => { handleGoBack() }, showReload: true, reloadCommand: () => { handleReload() } } }
			/>
			<Container fluid>
				<Row>
					<div className="col">
						<Card>
							<CardHeader>
								<div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
									<div style={{ flex: 1 }}>
										<SellerInventoryTabs
											value={ auctionsContext.sellerInventoryTab || 'auction' }
											onChange={ handleInventoryTabChange }
											counts={{
												// Use tab counts from API, fall back to current page totalRecords for active tab
												pending: tabCounts.pending ?? (auctionsContext.sellerInventoryTab === 'pending' ? pagingResult.totalRecords : null),
												auction: tabCounts.auction ?? (auctionsContext.sellerInventoryTab === 'auction' ? pagingResult.totalRecords : null),
												'bid-review': tabCounts['bid-review'] ?? (auctionsContext.sellerInventoryTab === 'bid-review' ? pagingResult.totalRecords : null),
												sold: tabCounts.sold ?? (auctionsContext.sellerInventoryTab === 'sold' ? pagingResult.totalRecords : null),
											}}
										/>
									</div>
									<Button
										color="link"
										onClick={ () => setShowFilters((s) => !s) }
										className="text-decoration-none"
										size="sm"
										style={{ paddingRight: 0, paddingLeft: 0, whiteSpace: 'nowrap' }}
									>
										<FontAwesomeIcon icon={ faFilter } /> { showFilters ? 'Hide Filters' : 'Show Filters' }
									</Button>
								</div>
							</CardHeader>
							<CardBody>
								<Row style={{ marginBottom: '0.5rem' }}>
									<Col md="12" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
										<Label style={{ marginBottom: 0, fontWeight: 600 }}>
											Showing { formatNumber(pagingResult.pageCollection.length) } of { formatNumber(pagingResult.totalRecords) } VINs
										</Label>
										<div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
											{/* BRD: Bulk Release Button - only on Auction tab when items selected */}
											{(auctionsContext.sellerInventoryTab || 'auction') === 'auction' && selectedAuctionIds.size > 0 && (
												<>
													<span className="text-muted" style={{ fontSize: '0.85em' }}>
														{selectedAuctionIds.size} selected
													</span>
													<Button
														color="primary"
														size="sm"
														onClick={() => setShowBulkReleaseModal(true)}
														style={{ whiteSpace: 'nowrap' }}
													>
														Bulk Release to Auction
													</Button>
													<Button
														color="link"
														size="sm"
														onClick={handleClearBulkSelection}
														style={{ whiteSpace: 'nowrap', padding: '0.25rem' }}
													>
														Clear
													</Button>
												</>
											)}
											{/* BRD Phase 6: Export to Excel for all tabs */}
											<Button
												color="success"
												size="sm"
												onClick={ handleExportTabData }
												style={{ whiteSpace: 'nowrap' }}
												disabled={ pagingResult.pageCollection.length === 0 }
											>
												<FontAwesomeIcon icon={ faFileExcel } /> Export to Excel
											</Button>
										</div>
									</Col>
								</Row>

								{/* Sold Tab Filters - Date Range & VIN/Buyer Search */}
								{ (auctionsContext.sellerInventoryTab || 'auction') === 'sold' && (
									<div style={{ borderBottom: '1px solid rgba(0,0,0,.1)', paddingBottom: '0.75rem', marginBottom: '0.75rem' }}>
										<Row>
											<Col md="8">
												<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
													<Label style={{ marginBottom: 0, fontSize: 'smaller', fontWeight: 500 }}>Sale Date:</Label>
													<DatePicker
														selected={ soldDateFrom }
														onChange={ (date) => setSoldDateFrom(date) }
														selectsStart
														startDate={ soldDateFrom }
														endDate={ soldDateTo }
														maxDate={ soldDateTo }
														dateFormat="MMM dd, yyyy"
														className="form-control form-control-sm"
														style={{ width: '130px' }}
													/>
													<Label style={{ marginBottom: 0, fontSize: 'smaller' }}>to</Label>
													<DatePicker
														selected={ soldDateTo }
														onChange={ (date) => setSoldDateTo(date) }
														selectsEnd
														startDate={ soldDateFrom }
														endDate={ soldDateTo }
														minDate={ soldDateFrom }
														maxDate={ new Date() }
														dateFormat="MMM dd, yyyy"
														className="form-control form-control-sm"
														style={{ width: '130px' }}
													/>
													<Button
														color="primary"
														size="sm"
														onClick={ handleSoldDateSearch }
													>
														Search
													</Button>
												</div>
											</Col>
											<Col md="4">
												<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
													<Label style={{ marginBottom: 0, fontSize: 'smaller', fontWeight: 500, whiteSpace: 'nowrap' }}>VIN/Buyer:</Label>
													<Input
														type="text"
														placeholder="Search VIN or Buyer..."
														value={ soldSearchText }
														onChange={ handleSoldSearchTextChange }
														bsSize="sm"
														style={{ minWidth: '150px' }}
													/>
												</div>
											</Col>
										</Row>
									</div>
								)}

								{ showFilters && (
									<div style={{ borderTop: '1px solid rgba(0,0,0,.05)', paddingTop: '0.75rem', marginBottom: '0.75rem' }}>
										<div style={{ marginBottom: '0.75rem' }}>
											<SellerTabStatusFilter
												parentTab={ auctionsContext.sellerInventoryTab || 'auction' }
												value={ auctionsContext.sellerTabStatusFilter || 'all' }
												onChange={ handleTabStatusFilterChange }
											/>
										</div>
										<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
											<Button
												color="link"
												onClick={ handleClearFilters }
												size="sm"
												className="text-decoration-none"
												style={{ paddingRight: 0, paddingLeft: 0 }}
											>
												Clear filters
											</Button>
										</div>
										{/* Hide advanced filters for Sold tab - it has date range filter instead */}
										{ (auctionsContext.sellerInventoryTab || 'auction') !== 'sold' && (
										<SearchAuctions
											viewModel={ viewModel }
											auctionStates={ auctionStates }
											handleStatusChange={ handleStatusChange }
											handleMakesChange={ handleMakesChange }
											handleModelsChange={ handleModelsChange }
											searchRequest={ auctionsContext.searchRequest }
											handleProvincesChange={ handleProvincesChange }
											handleSearchParameterChange={ handleSearchParameterChange }
											handleSearchAuctions={ handleSearchAuctions }
										/>
										)}
									</div>
								)}

								{ pagingResult.isModified &&
									<Row>
										<Col md="12">
											<Button block className="btn-block btn-default" size="lg" type="button" onClick={ handleUpdatePricing }>
												{ t('Auctions.Listing.Update_Pricing') }
											</Button>
										</Col>
									</Row>
								}
							</CardBody>
							<div className="auction-listing-table-wrap">
								<Table className="auction-listing-table">
									<thead className="thead-light">
										<tr>
											{activeColumns().map((col) => {
												const isSortable = !!col.sortKey
												const isActive = auctionsContext.searchRequest?.sortColumn === col.sortKey
												const direction = auctionsContext.searchRequest?.sortDirection || 'ASC'
												
												return (
													<th 
														key={ col.key } 
														className={ isSortable ? 'sort sortable-header' : 'sort' } 
														scope="col"
														style={ isSortable ? { cursor: 'pointer' } : {} }
														onClick={ isSortable ? () => handleColumnSort(col.sortKey) : undefined }
													>
														<div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
															{ col.header }
															{ isSortable && (
																<span className="sort-indicator" style={{ opacity: isActive ? 1 : 0.3 }}>
																	{ isActive ? (
																		direction === 'ASC' 
																			? <FontAwesomeIcon icon={ faSortUp } />
																			: <FontAwesomeIcon icon={ faSortDown } />
																	) : <FontAwesomeIcon icon={ faSort } /> }
																</span>
															)}
														</div>
													</th>
												)
											})}
										</tr>
									</thead>
									<tbody className="list">
										{pagingResult.pageCollection.map((pageItem, pageItemIndex) => (
											<tr key={ pageItem.auctionId }>
												{activeColumns().map((col) => (
													<React.Fragment key={ col.key }>
														{ col.cell(pageItem, pageItemIndex) }
													</React.Fragment>
												))}
											</tr>
										))}
									</tbody>
								</Table>
							</div>
							<CardFooter className="py-4">
								{ pagingResult.isModified &&
									<Row style={{ marginBottom: '10px' }}>
										<Col md="12">
											<Button block className="btn-block btn-default" size="lg" type="button" onClick={ handleUpdatePricing }>
												{ t('Auctions.Listing.Update_Pricing') }
											</Button>
										</Col>
									</Row>
								}
								{
									(pagingResult.pageCollection.length === 0) && (
										<Row style={{ marginBottom: '10px', width: '100%' }}>
											<Col md="12" style={{ textAlign: 'center' }}>
												<Label>{ t('Auctions.Listing.No_Results') }</Label>
											</Col>
										</Row>
									)
								}
								<div style={{ marginLeft: 'auto' }}>
									{
										(pagingResult.pageCollection.length > 0) && (
											<nav>
												<Paginator
													currentPage={ auctionsContext.searchRequest.pageIndex }
													pages={parseInt(((pagingResult.totalRecords - 1) / auctionsContext.searchRequest.pageSize) + 1, 10)}
													selectPage={handlePageChange}
												/>
											</nav>
										)
									}
								</div>
							</CardFooter>
						</Card>

						<Modal
							isOpen={ isAuctionDetailModalOpen }
							toggle={ closeAuctionDetailModal }
							size="xl"
							backdrop="static"
							fade={ false }
							style={{ maxWidth: '95vw' }}
						>
							<ModalHeader toggle={ closeAuctionDetailModal }>
								Auction Details
							</ModalHeader>
							<ModalBody>
								<div>
									{ renderAuctionDetailContent() }
								</div>
							</ModalBody>
						</Modal>
					</div>
				</Row>
			</Container>
			
			<MissingPricesModal
				openMissingPricesModal={ openMissingPricesModal }
				setOpenMissingPricesModal={ setOpenMissingPricesModal }
				priceValidationMessages={ priceValidationMessages }
			/>

			{/* Phase 6: Action Modals */}
			<HoldAuctionModal
				isOpen={showHoldModal}
				auction={actionTargetAuction}
				onClose={handleActionModalClose}
				onSuccess={handleActionModalSuccess}
			/>

			<VoidSaleModal
				isOpen={showVoidSaleModal}
				auction={actionTargetAuction}
				onClose={handleActionModalClose}
				onSuccess={handleActionModalSuccess}
			/>

			<PricingHistoryModal
				isOpen={showPricingHistoryModal}
				auction={actionTargetAuction}
				onClose={handleActionModalClose}
			/>

			{/* NEW: Bid Details Modal */}
			<BidDetailsModal
				isOpen={showBidDetailsModal}
				auction={actionTargetAuction}
				onClose={handleActionModalClose}
			/>

			{/* NEW: Counter Offer Modal */}
			<CounterOfferModal
				isOpen={showCounterOfferModal}
				auction={actionTargetAuction}
				counterAtReserve={counterAtReserve}
				onClose={handleActionModalClose}
				onSuccess={handleActionModalSuccess}
			/>

			{/* NEW: Sell Confirmation Modal */}
			<SellConfirmationModal
				isOpen={showSellConfirmationModal}
				auction={actionTargetAuction}
				onClose={handleActionModalClose}
				onSuccess={handleActionModalSuccess}
			/>

			{/* NEW: Relist Modal */}
			<RelistModal
				isOpen={showRelistModal}
				auction={actionTargetAuction}
				onClose={handleActionModalClose}
				onSuccess={handleActionModalSuccess}
			/>

			{/* BRD Phase 3: Move to Shipping Modal */}
			<MoveToShippingModal
				isOpen={showMoveToShippingModal}
				toggle={() => setShowMoveToShippingModal(false)}
				auction={actionTargetAuction}
				onSuccess={handleActionModalSuccess}
			/>

			{/* BRD Phase 3: Move to Physical Auction Modal */}
			<MoveToPhysicalAuctionModal
				isOpen={showMoveToPhysicalAuctionModal}
				toggle={() => setShowMoveToPhysicalAuctionModal(false)}
				auction={actionTargetAuction}
				onSuccess={handleActionModalSuccess}
			/>

			{/* BRD Phase 3: Unground Modal */}
			<UngroundModal
				isOpen={showUngroundModal}
				toggle={() => setShowUngroundModal(false)}
				auction={actionTargetAuction}
				onSuccess={handleActionModalSuccess}
			/>

			{/* BRD Phase 4: Release from Hold Modal */}
			<ReleaseFromHoldModal
				isOpen={showReleaseFromHoldModal}
				auction={actionTargetAuction}
				onClose={handleActionModalClose}
				onSuccess={handleActionModalSuccess}
			/>

			{/* BRD: Release to Auction Modal (from Awaiting Seller Release to Live) */}
			<ReleaseToAuctionModal
				isOpen={showReleaseToAuctionModal}
				auction={actionTargetAuction}
				onClose={handleActionModalClose}
				onSuccess={handleActionModalSuccess}
			/>

			{/* BRD: Bulk Release to Auction Modal */}
			<BulkReleaseToAuctionModal
				isOpen={showBulkReleaseModal}
				auctions={getSelectedAuctionsForBulkRelease()}
				onClose={() => setShowBulkReleaseModal(false)}
				onSuccess={() => {
					setShowBulkReleaseModal(false)
					handleClearBulkSelection()
					handleReload()
				}}
			/>

			{/* BRD: Cancel Auction Modal */}
			<CancelAuctionModal
				isOpen={showCancelAuctionModal}
				auction={actionTargetAuction}
				onClose={() => setShowCancelAuctionModal(false)}
				onSuccess={handleActionModalSuccess}
			/>
		</>
	)
}

export default AuctionsListing
